import { TableActionButton, TableActionGroup } from "@channelplay/table-actions";
import MoreFiltersDropdown from '../components/MoreFiltersDropdown';
import { InlineLoader } from '../components/loading';
import { FormErrorBanner } from '../components/FormErrorBanner';
import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, AlertCircle, X, Upload, Search, Download } from 'lucide-react';
import Modal from '../components/Modal';
import BulkUploadModal from '../components/BulkUploadModal';
import { storesAPI, categoriesAPI, locationsAPI, googleMapsAPI, storeCustomFieldsAPI, storeTradesAPI, storeIntelligenceAPI, type HiringLink, type HiringRequest, type StoreCustomField, type StoreTrade } from '../services/api';
import SearchableSelect from '../components/SearchableSelect';
import { normalizeLocationKey } from '../utils/locationKey';
import { buildRegionByStateKey, regionForStateName, stateMatchKeys } from '../utils/stateRegion';
import { useTableSort, SortTh, toEpochMs, type SortAccessors, type SortDir } from '../components/dashboard/useTableSort';

// Minimal shape of the store-intelligence summary this page needs: the ACTIVE
// deployed headcount per store (people.employees already excludes employees who have
// exited 1Office). Same source the dashboard's Deployment tab uses, so the
// "Undeployed" filter here reconciles with the dashboard's Undeployed Stores count.
interface StoreIntel {
  stores?: Record<string, { people?: { employees?: unknown[] } }>;
}
type DeploymentFilter = 'all' | 'deployed' | 'undeployed';

/** One entry from GET /api/stores/:id/timeline. Mirrors StoreTimelineEntry on the API. */
interface StoreTimelineEntry {
  id: string;
  /** ISO timestamp, or 'YYYY-MM-DD' when dateOnly is true. */
  at: string;
  dateOnly: boolean;
  eventType: 'store_created' | 'store_updated' | 'employee_deployed' | 'employee_undeployed';
  actorType: string;
  actorCode: string | null;
  actorName: string | null;
  changes: Array<{ field: string; label: string; from: string | null; to: string | null }>;
  note: string | null;
  employeeCode: string | null;
  employeeName: string | null;
}

const TIMELINE_DOT: Record<string, string> = {
  store_created: 'bg-indigo-500',
  store_updated: 'bg-sky-500',
  employee_deployed: 'bg-emerald-500',
  employee_undeployed: 'bg-rose-500',
};

/**
 * Deployment entries carry a bare DATE, so printing a time for them would invent
 * a 12:00 AM that never happened. The `T00:00:00` suffix keeps a date-only value
 * parsing as LOCAL midnight — without it the browser reads it as UTC and shows
 * the previous day in IST.
 */
const formatTimelineWhen = (at: string, dateOnly: boolean): string => {
  const d = new Date(dateOnly ? `${at}T00:00:00` : at);
  if (Number.isNaN(d.getTime())) return at;
  const date = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  return dateOnly
    ? date
    : `${date}, ${d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;
};

const timelineActorLabel = (e: StoreTimelineEntry): string => {
  if (e.actorType === 'bulk_upload') return e.actorName ? `Bulk upload · ${e.actorName}` : 'Bulk upload';
  return e.actorName || e.actorCode || 'System';
};
import StoreCustomFieldInputs from '../components/StoreCustomFieldInputs';
import { buildCsv, downloadCsv, CSV_UTF8_BOM } from '../utils/csv';
import { useAuthStore } from '../stores/authStore';
import { useFinancialAccess } from '../hooks/useFinancialAccess';
import { Skeleton } from '../components/Skeleton';
import {
  STORE_STATUS, STORE_STATUS_OPTIONS, STORE_STATUS_REDEPLOY, STORE_DISPLAY_STATUS_OPTIONS,
  STORE_STATUS_BADGE, STORE_STATUS_OTHER_BADGE, storeDisplayStatus,
  STAFFING_STATUS_OPTIONS, STAFFING_STATUS_BADGE, STAFFING_STATUS_UNSET_BADGE,
} from '../constants/storeStatus';
import { GooglePlacesSession } from '../utils/googlePlacesSession';
import { StoreHiringDot } from '../components/hiring/StoreHiringDot';
import { HiringPanel } from '../components/hiring/HiringPanel';
import { LinkHiringModal } from '../components/hiring/LinkHiringModal';
import { RaiseHiringModal, type RaiseHiringSubject } from '../components/hiring/RaiseHiringModal';
import { HiringRequestWindow } from '../components/hiring/HiringRequestWindow';
import { forgetHiringAccess, hiringUserKeyOf, useHiringAccess } from '../components/hiring/useHiringAccess';
import { forgetHiringRequests, useHiringRequests } from '../components/hiring/useHiringRequests';
import { NO_HIRING_LINKS, storeHiringKey, useStoreHiring } from '../components/hiring/useStoreHiring';
import { useReadAgainOnReturn } from '../components/hiring/useReadAgainOnReturn';
import { RAISE_WORDS, REQUEST_WORDS, STORE_ADDED_WORDS, formatHiringDay, hasRaiseInFlight } from '../components/hiring/hiringText';
import { newStoreRaiseMode, type NewStoreRaiseMode } from '../components/hiring/newStoreRaise';
import { STORE_ROW_HIRING_WORDS, storeOpenHiringOf } from '../components/hiring/useStoreRowHiring';
import { useDeployHiringCheck } from '../components/hiring/useDeployHiringCheck';
import {
  NO_STORE_PEOPLE, personHeadline, storePeopleDownloadCells, storePeopleOf, storePeopleSearchTerms,
  type StorePerson,
} from '../components/stores/storePeople';
import { DeployedEmployeeCell, ReportingManagerCell } from '../components/stores/StorePeopleCells';

interface StoreCategory {
  id: number;
  category_name: string;
  payout_min: number;
  payout_max: number;
  eligibility_criteria?: string;
  seed_level: number;
  status?: string;
}

interface StoreRecord {
  id: string;
  trade: string;
  name: string;
  storeCode: string;
  category: string;
  categoryId: number | null;
  /** The DISPLAY status (`status_display`), not the stored column: a store awaiting
   *  redeployment is stored 'Active' and reads 'Outside Approved – Redeploy' here.
   *  A plain string because store_status is free text — production also holds 'Notice'. */
  status: string;
  /** The stored column itself (stores.store_status). '' on an old row that has none —
   *  such a row DISPLAYS as Active but is not Active to any query, see statusBaselineOf. */
  storedStatus: string;
  /** Manpower-pipeline status — a SECOND axis, independent of `status`. '' = not set.
   *  This is the STORED value: what the edit form offers and what a save writes back. */
  staffingStatus: string;
  /** The same status read against the LIVE roster: an automatic 'Deployed' becomes
   *  'Hiring to be done' the moment the last active employee leaves, without waiting for
   *  the reconcile sweep. What the list shows and filters on. */
  staffingStatusEffective: string;
  address: string;
  city: string;
  state: string;
  /** 1Office region for `state`, resolved server-side. '' until the backfill runs. */
  region: string;
  /** 1Office tier for `city`, resolved server-side. '' until the backfill runs. */
  tier: string;
  pincode: string;
  latitude: string;
  longitude: string;
  attendanceRadius?: number;
  shiftStartTime?: string | null;
  storeWeekOff?: number | null;
  compliancePolicy?: string;
  /** Project-defined extra attributes, keyed by custom field id. */
  customFields?: Record<string, unknown>;
  lastUpdated: string;
  /** `updated_at` as epoch ms — what the Last Updated column SORTS on. `lastUpdated`
   *  is a D/M/YYYY display string, which orders wrongly as text. null when unknown. */
  updatedAtMs: number | null;
  employeeName?: string;
  employeeCode?: string;
  designation?: string;
  dateOfJoining?: string;
  salary?: number;
  payoutMin?: number;
  payoutMax?: number;
  eligibilityCriteria?: string;
  seedLevel?: number;
  // New fields from store_employees junction table
  employees?: Array<{
    employee_code?: string;
    employee_name?: string;
    /** Frozen 1Office budget name — kept for the deploy modal, NOT exported. See headers. */
    designation?: string;
    /** Live ChannelPlex project role (user_roles -> roles.name). null when unassigned. */
    role_name?: string | null;
    reporting_manager_name?: string | null;
    reporting_manager_code?: string | null;
    date_of_joining?: string | null;
    salary?: number | string | null;
    ipro_status?: string;
    seed_score?: number | null;
    is_primary?: boolean;
    /** Still on the 1Office roster (GET /stores). false = exited, the mapping a leftover. */
    is_active?: boolean | null;
  }>;
  employeeCount?: number;
  // Effective per-category minimum headcount (≥1, default 1). Drives the min-aware
  // "Undeployed" filter (active headcount < minEmployees = vacant OR understaffed).
  minEmployees: number;
}

interface AvailableEmployee {
  name: string;
  code: string;
  ctc: string;
  join: string;
  designation?: string;
  city?: string;
  state?: string;
  /** Role allows several stores at once — already deployed elsewhere, still offerable here. */
  multiStore?: boolean;
  /** Serving notice: their last working day ('YYYY-MM-DD', today or later). The server lists nobody who has left. */
  leavingOn?: string | null;
}

interface DeployedEmployee {
  name: string;
  code: string;
  role: string;
  salary: string;
  joined: string;
  /** Still on the 1Office roster. false = they have exited and this mapping is a leftover. */
  isActive: boolean;
}

/** One person on a store, as GET /stores and GET /stores/:id answer them. */
type StoreEmployeeRow = NonNullable<StoreRecord['employees']>[number] & { name?: string; code?: string };

/** An employee code as two answers are matched on it. */
const employeeCodeKey = (code: unknown): string => String(code ?? '').trim().toLowerCase();

/**
 * The Deploy Employee window's list of people, from a store's `employees`. GET /stores/:id
 * does not carry the roster flag: `activeOf` then gives it (the page's row copy's flag), and
 * someone it does not know reads as active, as an answer without the flag always has.
 */
const deployedEmployeesOf = (
  employees: readonly StoreEmployeeRow[],
  activeOf: (code: string) => boolean | undefined = () => undefined,
): DeployedEmployee[] => employees.map((emp) => {
  const code = emp.employee_code || emp.code || '—';
  return {
    name: emp.employee_name || emp.name || 'Unknown',
    code,
    role: emp.designation || 'Full Time',
    salary: emp.salary ? `₹${Number(emp.salary).toLocaleString('en-IN')}/mo` : '—',
    joined: emp.date_of_joining ? new Date(emp.date_of_joining).toLocaleDateString('en-IN') : '—',
    // Only an explicit false means "has left 1Office": an older API that does not send
    // the flag must keep reading as deployed rather than mark the whole store exited.
    isActive: typeof emp.is_active === 'boolean' ? emp.is_active : activeOf(code) ?? true,
  };
});

/** A store as the Raise hiring window is given it: what its form shows about the store. */
const raiseSubjectOf = (store: StoreRecord): RaiseHiringSubject => ({
  kind: 'store',
  storeId: store.id,
  storeName: store.name,
  storeCode: store.storeCode,
  address: store.address,
  city: store.city,
  state: store.state,
  pincode: store.pincode,
  hasMapLocation: !!String(store.latitude ?? '').trim() && !!String(store.longitude ?? '').trim(),
  status: store.status,
  staffingStatus: store.staffingStatusEffective || store.staffingStatus,
});

/** A store with no hiring request waiting: one shared empty list. */
const NO_HIRING_REQUESTS: readonly HiringRequest[] = [];
const waitingRequestsOf = (request: HiringRequest | undefined): readonly HiringRequest[] =>
  (request ? [request] : NO_HIRING_REQUESTS);
/** Before the hiring list is read, a row offers no "Open hiring". */
const NO_OPEN_HIRING: ReturnType<typeof storeOpenHiringOf> = { status: null, openHover: null };

const defaultFiltersConfig = {
  trade: ['LFR', 'Apple Premium', 'GT Stores'],
  category: ['Base', 'L0', 'L1', 'L2', 'L3'],
  state: ['Uttar Pradesh', 'Haryana', 'Telangana', 'Maharashtra', 'Karnataka', 'Tamil Nadu', 'Delhi'],
  status: STORE_DISPLAY_STATUS_OPTIONS,
  staffingStatus: STAFFING_STATUS_OPTIONS,
};

/** Shown under a Status select while 'Outside Approved – Redeploy' is the choice. */
const REDEPLOY_STATUS_HELP =
  `The store stays active and its employees keep marking attendance. They are listed under Dashboard › Deployment › ${STORE_STATUS_REDEPLOY} until they are moved. The status returns to Active when the last employee is moved out.`;

/**
 * The status a store's two status forms measure "did the user change it?" against.
 * Normally what the store displays. Empty when nothing is stored: the form shows
 * Active, and saving must WRITE Active — the repair every save used to make, before
 * the forms stopped re-posting a status the user had not touched.
 */
const statusBaselineOf = (store: { status: string; storedStatus: string }): string =>
  (store.storedStatus.trim() ? store.status : '');

/** Filter value for "no staffing status set". Not a real status, so it cannot collide
 *  with one — an empty filter already means "All". */
const STAFFING_UNSET = '__unset__';

/** Stores columns that sort newest-first on their first click; every other column is
 *  text and starts A→Z. */
const storeFirstSortDir = (key: string): SortDir => (key === 'lastUpdated' ? 'desc' : 'asc');

/**
 * A store row as the server sends it (GET /stores, and the store POST /stores answers with)
 * as this page holds a store.
 */
const storeRecordOf = (store: any): StoreRecord => ({
  id: store.id,
  trade: store.trade || '',
  name: store.store_name || '',
  storeCode: store.store_code || '',
  category: store.category_name || '',
  categoryId: store.store_category || store.category_id || null,
  // The display status, not the raw column: a store awaiting redeployment is
  // stored 'Active'. Everything below — badge, filter, sort, download, and the
  // values the two status forms open with — reads this one field.
  status: storeDisplayStatus(store),
  storedStatus: store.store_status || '',
  staffingStatus: store.staffing_status || '',
  // What the badge should read today: the server demotes an automatic 'Deployed'
  // to 'Hiring to be done' as soon as the last active employee leaves 1Office,
  // without waiting for the reconcile sweep to unmap them. `staffingStatus` stays
  // the STORED value, so the edit form still offers what is really on the record.
  staffingStatusEffective: store.staffing_status_effective ?? store.staffing_status ?? '',
  address: store.address || '',
  city: store.city || '',
  state: store.state || '',
  // Derived server-side from state/city and stored on the row. Blank until the
  // backfill has run, which is why the Region cell still falls back to resolving
  // from the state master client-side.
  region: store.region || '',
  tier: store.tier || '',
  pincode: store.pin_code || '',
  latitude: store.latitude || '',
  longitude: store.longitude || '',
  attendanceRadius: store.attendance_radius ? Number(store.attendance_radius) : 100,
  shiftStartTime: store.shift_start_time ? String(store.shift_start_time).slice(0, 5) : null,
  storeWeekOff: store.store_week_off ?? null,
  compliancePolicy: store.compliance_policy || '',
  customFields: (store.custom_fields as Record<string, unknown>) || {},
  lastUpdated: store.updated_at ? new Date(store.updated_at).toLocaleDateString('en-IN') : '-',
  updatedAtMs: toEpochMs(store.updated_at),
  employeeName: store.employee_name || '',
  employeeCode: store.employee_code || '',
  designation: store.designation || '',
  dateOfJoining: store.date_of_joining || '',
  salary: store.salary ? Number(store.salary) : 0,
  payoutMin: store.payout_min ? Number(store.payout_min) : undefined,
  payoutMax: store.payout_max ? Number(store.payout_max) : undefined,
  eligibilityCriteria: store.eligibility_criteria || '',
  seedLevel: store.seed_level ? Number(store.seed_level) : undefined,
  // Include employees array from store_employees junction table
  employees: store.employees || [],
  employeeCount: store.employee_count || (store.employees?.length ?? 0),
  // Effective per-category minimum headcount (default 1). A store counts as
  // "Undeployed" once active headcount is BELOW this — matching the dashboard.
  minEmployees: Number.isInteger(store.min_employees) && Number(store.min_employees) >= 1 ? Number(store.min_employees) : 1,
});

/**
 * The PIN code Add New Store saves (spaces dropped), or '' when it is not 6 digits whose
 * first is 1 to 9. The server holds a new store to the same rule.
 */
const newStorePinCode = (raw: string): string => {
  const compact = raw.replace(/\s+/g, '');
  return /^[1-9]\d{5}$/.test(compact) ? compact : '';
};

// City data type from API
interface CityData {
  name: string;
  pincode: string | null;
  /** 1Office city tier ("Tier 1" / "Tier 2" …). Absent on store-only cities. */
  tierName: string | null;
}

// State data type from API
interface StateData {
  stateId: number;
  stateName: string;
  countryId: number;
  countryName: string;
  /** 1Office region the state rolls up to ("North" / "East" …). Optional: a
   *  misspelled store state has no master row and so no region. */
  regionId?: number;
  regionName?: string;
}

// Exactly what the autocomplete endpoint returns — no coordinates and no pincode.
// Those only arrive from the follow-up Place Details call, so they are deliberately
// absent here rather than carried as always-empty strings.
interface AddressSuggestion {
  address: string;
  placeId: string;
}

export default function StoresPage({
  embedded, actionSlot, onCount, initialSearch = '',
}: {
  embedded?: boolean;
  actionSlot?: HTMLElement | null;
  onCount?: (n: number) => void;
  /** What the search box starts with: the ?q= of a link to this page, read by StoreMasterPage. */
  initialSearch?: string;
} = {}) {
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);
  // The project on show NOW. A callback keeps the project it was made for; an answer that
  // arrives after the page has moved to another project is held against this before it
  // reads anything for the project it came from.
  const projectOnShow = useRef(selectedProjectId);
  projectOnShow.current = selectedProjectId;
  // Payout bands and employee CTC are granted per user (financial_visibility_users);
  // the API redacts them regardless, so this only decides whether to render the
  // surrounding chrome or leave it out entirely.
  const canSeeMoney = useFinancialAccess(selectedProjectId);
  // Hiring: a Recruitment opening followed against a store. Dark unless the server says
  // this project has it switched on AND this user may see it; until that answer is in
  // hand, and on any failure of it, `hiringOn` is false and nothing below draws — no
  // column, no control, no message. The whole column is one request, never one per store.
  const hiringUserKey = useAuthStore((state) => hiringUserKeyOf(state.user));
  const hiringAccess = useHiringAccess(selectedProjectId, hiringUserKey);
  const hiringOn = hiringAccess.canView;
  const canManageHiring = hiringAccess.canManage;
  const storeHiring = useStoreHiring(selectedProjectId, hiringOn);
  // Raise hiring (ChannelPlex asks Recruitment to create an opening): only for a person who
  // holds "Raise & cancel hiring" (the access answer's `canRaise`, a row of its own in Access
  // Management) and when the hiring list itself says Raise is on and they may raise here.
  // Off, not allowed, not answered yet, or a server that does not say: no control.
  const [raiseGoneFor, setRaiseGoneFor] = useState<number | null>(null);
  const canRaiseHiring = hiringAccess.canRaise && storeHiring.raise.canRaise && raiseGoneFor !== selectedProjectId;
  // Hiring requests (one person asks for hiring to be raised, another confirms). Asked
  // for only when the access answer says they exist for this person here; nothing of
  // them is drawn until their list has answered. A person with Hiring (view) alone, and
  // every project with Raise off, never makes the call and sees the page as it was.
  const hiringRequests = useHiringRequests(selectedProjectId, hiringUserKey, hiringAccess.canSeeRequests);
  const requestsShown = hiringOn && hiringRequests.status === 'ready';
  // A person who may send a hiring request and does not hold "Raise & cancel hiring": they ask.
  const canAskHiring = requestsShown && hiringRequests.can.send && !hiringAccess.canRaise;
  const reloadStoreHiring = storeHiring.reload;
  const reloadHiringRequests = hiringRequests.reload;
  // Something changed that either list may not show yet: both are read again.
  const reloadHiringLists = useCallback(() => {
    reloadStoreHiring();
    reloadHiringRequests();
  }, [reloadStoreHiring, reloadHiringRequests]);
  // Hiring linked or raised in another tab (Undeployed Stores opens this page in one, and
  // Dashboard › Hiring may be open in another): both lists are read again when the person
  // comes back to this tab, so the rows show it without a reload.
  useReadAgainOnReturn(hiringOn, reloadHiringLists);
  // "Open hiring for …" on a store's row (product owner, 16 Oct 2026: after linking an opening to
  // a store, "user can open that opening and can see all details in it - and candidates and
  // everything", without going to Dashboard › Hiring): the word the store's Hiring cell reads,
  // from the same list, and nothing before that list is read. Read off the list as it is now,
  // so a link, raise, ask or stop made from this page shows on the row at once.
  const openHiringOf = (store: Pick<StoreRecord, 'id'>) => (storeHiring.status === 'ready'
    ? storeOpenHiringOf(
      storeHiring.byStore.get(storeHiringKey(store.id)) ?? NO_HIRING_LINKS,
      requestsShown ? hiringRequests.byStore.get(storeHiringKey(store.id)) ?? null : null,
    )
    : NO_OPEN_HIRING);

  // Data states
  const [stores, setStores] = useState<StoreRecord[]>([]);
  const [categories, setCategories] = useState<StoreCategory[]>([]);
  const [availableEmployees, setAvailableEmployees] = useState<AvailableEmployee[]>([]);
  const [deployedEmployees, setDeployedEmployees] = useState<DeployedEmployee[]>([]);

  // Location data from API - store full state objects to access stateId
  const [apiStates, setApiStates] = useState<StateData[]>([]);
  const [citiesByState, setCitiesByState] = useState<Record<string, CityData[]>>({});
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    trade: '',
    category: '',
    state: '',
    city: '',
    status: '',
    staffingStatus: '',
  });
  // Starts on what a link asked for, so a row on the Data Hygiene dashboard opens on its
  // store. Only the start: the box is the person's from then on, and a later change of
  // the prop is not read.
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  // Deployment filter (All / Deployed / Undeployed). Undeployed = an OPEN store with
  // no ACTIVE employee — the same definition the dashboard's Undeployed Stores card
  // uses. The active-headcount data is loaded lazily (see the effect below) so the
  // default store list load pays nothing for it.
  const [deploymentFilter, setDeploymentFilter] = useState<DeploymentFilter>('all');
  const [intelligence, setIntelligence] = useState<StoreIntel | null>(null);
  const [intelLoading, setIntelLoading] = useState(false);
  // Set once we've tried to load intelligence for this project, so a failed load
  // doesn't retry in a loop (the filter then degrades to the roster count).
  const [intelAttempted, setIntelAttempted] = useState(false);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [statusModalStore, setStatusModalStore] = useState<StoreRecord | null>(null);
  const [statusFormState, setStatusFormState] = useState('');
  const [staffingFormState, setStaffingFormState] = useState('');
  const [statusRemark, setStatusRemark] = useState('');
  const [timeline, setTimeline] = useState<StoreTimelineEntry[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState<string | null>(null);
  // Guards against a slow response for a previously-opened store landing after a
  // faster one for the store the user is looking at now.
  const timelineReqRef = useRef(0);
  const [selectedCandidateCode, setSelectedCandidateCode] = useState<string | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingStoreId, setEditingStoreId] = useState<string | null>(null);
  // The Status the Edit Store form was opened with — a save sends store_status only
  // when the user moved it off this (see handleStoreSubmit). '' for a store with no
  // stored status (statusBaselineOf), so that one always sends it.
  const editOpenedStatusRef = useRef<string | null>(null);
  const [deployModalStore, setDeployModalStore] = useState<StoreRecord | null>(null);
  // The Status that modal shows, read off the list as it is NOW rather than the row it
  // was opened with: Remove can empty the store, and the server then returns it from
  // "Outside Approved – Redeploy" to Active while the modal is still open.
  const deployModalStatus = useMemo(
    () => stores.find((s) => s.id === deployModalStore?.id)?.status ?? deployModalStore?.status,
    [stores, deployModalStore],
  );
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  // The deployed employee whose Remove was clicked and is waiting for the "Yes, remove"
  // click. One at a time; null when no row is asking.
  const [removeConfirmCode, setRemoveConfirmCode] = useState<string | null>(null);
  // The store whose Deploy Employee modal is open right now, null when it is closed. A
  // ref, not state: a Remove or Deploy that answers late has to see that the modal it
  // was clicked in has since been closed, or opened again on another store.
  const deployModalOpenForRef = useRef<string | null>(null);
  // Where that modal's "Currently Deployed Employees" came from: the page's row while the store
  // is read afresh ('checking'), that fresh read ('fresh'), or the row alone once it failed ('row').
  const [deployedRead, setDeployedRead] = useState<'checking' | 'fresh' | 'row'>('row');
  // The latest fresh read: an earlier one that answers late is dropped.
  const deployedReadSeqRef = useRef(0);
  // People removed in the modal while its fresh read was out: that answer may predate the removal.
  const removedSinceReadRef = useRef<Set<string>>(new Set());
  // A failure of that modal's own Remove or Deploy. Kept apart from formError, which the
  // Edit Store form and the Status dialog share, so a late failure from one of those
  // never appears in this modal and one from here never appears in theirs.
  const [deployModalError, setDeployModalError] = useState<string | null>(null);
  // The employee whose "Remove from this store?" just closed without a removal. Their
  // Remove button takes keyboard focus back when it returns to the page.
  const refocusRemoveRef = useRef<string | null>(null);
  // The store whose hiring panel is open, and the store being linked to an opening.
  const [hiringPanelStore, setHiringPanelStore] = useState<StoreRecord | null>(null);
  const [linkHiringStore, setLinkHiringStore] = useState<StoreRecord | null>(null);
  // The store hiring is being raised for (the Raise hiring window).
  const [raiseHiringStore, setRaiseHiringStore] = useState<StoreRecord | null>(null);
  // "Link it instead", from the Raise hiring window: the budget and the unlinked opening
  // Recruitment already has for the store, for the Link hiring window to open on. It is
  // cleared whenever that window closes, so Link hiring opened any other way starts empty.
  const [linkHiringPick, setLinkHiringPick] = useState<{ budgetId: number; openingId: number } | null>(null);
  // The store a hiring request is being filled in for (the Raise hiring window in ask
  // mode), and the hiring request whose window is open.
  const [askHiringStore, setAskHiringStore] = useState<StoreRecord | null>(null);
  const [hiringRequestOpen, setHiringRequestOpen] = useState<HiringRequest | null>(null);
  // "Raise again" (or "Ask for hiring") on an opening deleted in Recruitment, from the store's
  // hiring window: that link, which a raise sent from the window it opens stops following.
  // Only for its own store's window, and gone once neither window is open.
  const [hiringReplacing, setHiringReplacing] = useState<{ storeId: string; link: HiringLink } | null>(null);
  useEffect(() => {
    if (!raiseHiringStore && !askHiringStore) setHiringReplacing(null);
  }, [raiseHiringStore, askHiringStore]);
  const replacingFor = (store: StoreRecord): HiringLink | undefined =>
    (hiringReplacing && hiringReplacing.storeId === store.id ? hiringReplacing.link : undefined);
  // "Store added": Add New Store made a NEW store (the server answered 201), and hiring may
  // be raised, or asked for, for it here. The window turns into this step in place of
  // closing. Kept with the project the store was added in.
  const [storeAdded, setStoreAdded] = useState<{ store: StoreRecord; projectId: number } | null>(null);
  // The store the Raise hiring window was opened for from that step: the window opens with
  // the reason New store. Matched by the record itself, so a window opened from a row
  // never carries it.
  const [newStoreToRaise, setNewStoreToRaise] = useState<StoreRecord | null>(null);
  /**
   * A store as the list has it now: the Raise hiring window's store card shows this, so a
   * save in the Edit Store window opened from that card ("Edit store") shows on the card
   * once the list is read again. A store the list no longer holds is shown as it was.
   */
  const storedNow = (store: StoreRecord): StoreRecord => stores.find((s) => s.id === store.id) ?? store;
  // Hiring went off for the project while the page was open (a hiring call answered
  // "not enabled"), or the page moved to another project: its windows close with it,
  // and nothing is said.
  useEffect(() => {
    setHiringPanelStore(null);
    setLinkHiringStore(null);
    setLinkHiringPick(null);
    setRaiseHiringStore(null);
    setAskHiringStore(null);
    setHiringRequestOpen(null);
  }, [hiringOn, selectedProjectId]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const addressSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const addressSearchAbortRef = useRef<AbortController | null>(null);
  const addressDetailsAbortRef = useRef<AbortController | null>(null);
  const addressSearchSequenceRef = useRef(0);
  const addressSuggestionsSessionTokenRef = useRef<string | null>(null);
  const placesSessionRef = useRef(new GooglePlacesSession());
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [isLoadingAddressSuggestions, setIsLoadingAddressSuggestions] = useState(false);
  
  const [formState, setFormState] = useState<{
    trade: string;
    name: string;
    storeCode: string;
    categoryId: string;
    status: string;
    staffingStatus: string;
    address: string;
    state: string;
    city: string;
    pincode: string;
    latitude: string;
    longitude: string;
    attendanceRadius: string;
    shiftStartTime: string;
    storeWeekOff: string;
    compliancePolicy: string;
  }>({
    trade: '',
    name: '',
    storeCode: '',
    categoryId: '',
    status: STORE_STATUS.ACTIVE,
    staffingStatus: '',
    address: '',
    state: '',
    city: '',
    pincode: '',
    latitude: '',
    longitude: '',
    attendanceRadius: '100',
    shiftStartTime: '',
    storeWeekOff: '',
    compliancePolicy: '',
  });

  // Project-defined extra store attributes (Master Data → Stores → Custom Fields).
  // Values are held apart from formState rather than folded into it: they are keyed
  // by field id and hold mixed types (string | number | boolean | string[]), which
  // the typed formState shape cannot express.
  const [customFieldDefs, setCustomFieldDefs] = useState<StoreCustomField[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, unknown>>({});

  // The governed trade list (Master Data → Stores → Trades). Drives the Add/Edit
  // form only — the FILTER dropdown still lists trades present in the data, since
  // filtering by a trade no store has would just return nothing.
  const [tradeOptions, setTradeOptions] = useState<string[]>([]);

  /**
   * Trades offered by the Add/Edit Store dropdown.
   *
   * The master list, plus two safety nets:
   *  - the store being edited keeps its own trade as an option even if that trade
   *    has since been retired. Without this the <select> finds no matching option,
   *    renders blank, and saving silently clears the store's trade.
   *  - if the master is empty or unreachable, fall back to the trades present in
   *    the data — the previous behaviour — so the form is never left with nothing.
   */
  const formTradeOptions = useMemo(() => {
    const base = tradeOptions.length > 0
      ? tradeOptions
      : [...new Set(stores.map((s) => s.trade).filter(Boolean))].sort();
    const current = formState.trade?.trim();
    if (current && !base.some((t) => t.toLowerCase() === current.toLowerCase())) {
      return [...base, current];
    }
    return base;
  }, [tradeOptions, stores, formState.trade]);

  // Dynamic filter options from data
  const filtersConfig = useMemo(() => {
    const uniqueTrades = [...new Set(stores.map(s => s.trade).filter(Boolean))].sort();
    const uniqueCategories = [...new Set(categories.map(c => c.category_name).filter(Boolean))].sort();

    // Fold state variants onto one entry, preferring the canonical 1Office master spelling
    // when it exists. This removes the duplicate "Kerala"/"KERALA" rows without touching data.
    const stateByKey = new Map<string, string>();
    for (const name of apiStates.map(s => s.stateName).filter(Boolean)) {
      const k = normalizeLocationKey(name);
      if (!stateByKey.has(k)) stateByKey.set(k, name);
    }
    for (const name of stores.map(s => s.state).filter(Boolean) as string[]) {
      const k = normalizeLocationKey(name);
      if (!stateByKey.has(k)) stateByKey.set(k, name);
    }
    const allStates = [...stateByKey.values()].sort();

    // Same case-insensitive fold for the "all cities" list.
    const cityByKey = new Map<string, string>();
    for (const name of stores.map(s => s.city).filter(Boolean) as string[]) {
      const k = normalizeLocationKey(name);
      if (!cityByKey.has(k)) cityByKey.set(k, name);
    }
    const uniqueCities = [...cityByKey.values()].sort();

    return {
      trade: uniqueTrades.length > 0 ? uniqueTrades : defaultFiltersConfig.trade,
      category: uniqueCategories.length > 0 ? uniqueCategories : defaultFiltersConfig.category,
      state: allStates.length > 0 ? allStates : defaultFiltersConfig.state,
      status: defaultFiltersConfig.status,
      // Always the full governed list, never derived from the data: a status no
      // store currently holds is exactly the one you need to be able to set.
      staffingStatus: defaultFiltersConfig.staffingStatus,
      city: uniqueCities,
    };
  }, [stores, categories, apiStates]);

  // Canonical 1Office master states, keyed by normalized name — the single source of truth
  // that dirty store strings get folded onto.
  const canonicalStateByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of apiStates) {
      const name = (s.stateName || '').trim();
      if (name) map.set(normalizeLocationKey(name), name);
    }
    return map;
  }, [apiStates]);

  // Map any (possibly dirty) state string to its canonical master spelling when one exists,
  // including through a full-name synonym ("Puducherry" -> the master's "Pondicherry").
  const canonicalizeState = useCallback(
    (name: string): string => {
      for (const key of stateMatchKeys(name)) {
        const canonical = canonicalStateByKey.get(key);
        if (canonical) return canonical;
      }
      return name;
    },
    [canonicalStateByKey]
  );

  // Region each master state rolls up to, keyed by normalized state name. A store saved
  // under a misspelled state has no master row, so it simply resolves to no region.
  const regionByStateKey = useMemo(() => buildRegionByStateKey(apiStates), [apiStates]);

  // A store's region, or undefined when its state has no master row. Matched through
  // normalizeLocationKey so a store stored as "KERALA" still resolves to South.
  const regionForStore = useCallback(
    (stateRaw?: string | null): string | undefined => regionForStateName(regionByStateKey, stateRaw),
    [regionByStateKey]
  );

  // The Add/Edit form only offers canonical master states (no dirty duplicates), so every
  // pick resolves to a stateId and returns the full city list. When editing a store whose
  // saved state isn't in the master (a true misspelling), keep it as a fallback option so
  // the select doesn't blank out.
  const formStateOptions = useMemo(() => {
    const names = apiStates.map(s => (s.stateName || '').trim()).filter(Boolean);
    const current = (formState.state || '').trim();
    if (current && !names.some(n => normalizeLocationKey(n) === normalizeLocationKey(current))) {
      names.push(current);
    }
    return [...new Set(names)].sort();
  }, [apiStates, formState.state]);

  // Merge API + store cities for a selected state, matching stores case-insensitively so a
  // canonical state still surfaces cities from records saved under a variant spelling.
  // Canonical (API) casing wins; store-only cities are appended; the currently-selected city
  // is always kept selectable with its stored casing so edit never blanks out.
  const buildCityList = useCallback(
    (stateName: string, selectedCity?: string): string[] => {
      const targetKey = normalizeLocationKey(stateName);
      const apiCities = (citiesByState[stateName] || []).map(c => c.name).filter(Boolean);
      const seen = new Set(apiCities.map(normalizeLocationKey));
      const merged = [...apiCities];
      for (const s of stores) {
        if (normalizeLocationKey(s.state) !== targetKey || !s.city) continue;
        const k = normalizeLocationKey(s.city);
        if (!seen.has(k)) { seen.add(k); merged.push(s.city); }
      }
      const current = (selectedCity || '').trim();
      if (current) {
        const ck = normalizeLocationKey(current);
        const idx = merged.findIndex(c => normalizeLocationKey(c) === ck);
        if (idx === -1) merged.push(current); else merged[idx] = current;
      }
      return merged.sort();
    },
    [stores, citiesByState]
  );

  // Get available cities based on selected state filter
  const availableCities = useMemo(() => {
    if (filters.state) {
      return buildCityList(filters.state);
    }
    // If no state selected, show all cities from stores
    return filtersConfig.city;
  }, [filters.state, filtersConfig.city, buildCityList]);

  // Get available cities for form based on selected state in form
  const formAvailableCities = useMemo(() => {
    if (formState.state) {
      return buildCityList(formState.state, formState.city);
    }
    return [];
  }, [formState.state, formState.city, buildCityList]);

  // Tier of each master city in the selected state, keyed by normalized city name.
  // Store-only cities (merged in by buildCityList) aren't in the master list and so
  // carry no tier — they render bare. Matched case-insensitively because the cities
  // are cached under whichever spelling of the state triggered the fetch.
  const formCityTierByKey = useMemo(() => {
    const map = new Map<string, string>();
    const target = normalizeLocationKey(formState.state || '');
    if (!target) return map;
    for (const [stateName, list] of Object.entries(citiesByState)) {
      if (normalizeLocationKey(stateName) !== target) continue;
      for (const c of list) {
        if (c.name && c.tierName) map.set(normalizeLocationKey(c.name), c.tierName);
      }
    }
    return map;
  }, [citiesByState, formState.state]);

  const resetAddressAutocomplete = useCallback(() => {
    if (addressSearchTimeoutRef.current) {
      clearTimeout(addressSearchTimeoutRef.current);
      addressSearchTimeoutRef.current = null;
    }
    addressSearchAbortRef.current?.abort();
    addressSearchAbortRef.current = null;
    addressDetailsAbortRef.current?.abort();
    addressDetailsAbortRef.current = null;
    addressSearchSequenceRef.current += 1;
    addressSuggestionsSessionTokenRef.current = null;
    placesSessionRef.current.reset();
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
    setIsLoadingAddressSuggestions(false);
  }, []);

  // Fetch address suggestions based on input
  const fetchAddressSuggestions = useCallback(async (query: string) => {
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    addressSearchAbortRef.current?.abort();
    const abortController = new AbortController();
    addressSearchAbortRef.current = abortController;
    const requestSequence = ++addressSearchSequenceRef.current;
    const sessionToken = placesSessionRef.current.getToken();

    setIsLoadingAddressSuggestions(true);
    try {
      const response = await googleMapsAPI.autocomplete({
        input: normalizedQuery,
        city: formState.city || undefined,
        state: formState.state || undefined,
        sessionToken,
      }, abortController.signal);

      if (
        abortController.signal.aborted ||
        requestSequence !== addressSearchSequenceRef.current
      ) {
        return;
      }

      if (response.data?.status === 'success') {
        const suggestions: AddressSuggestion[] = (response.data.data || []).map((item: any) => ({
          address: item.address,
          placeId: item.placeId,
        }));
        addressSuggestionsSessionTokenRef.current = sessionToken;
        setAddressSuggestions(suggestions);
        setShowAddressSuggestions(suggestions.length > 0);
      } else {
        addressSuggestionsSessionTokenRef.current = null;
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
      }
    } catch (err: unknown) {
      const cancelled =
        abortController.signal.aborted ||
        (typeof err === 'object' && err !== null && 'code' in err && err.code === 'ERR_CANCELED');
      if (cancelled || requestSequence !== addressSearchSequenceRef.current) return;
      console.error('Failed to fetch address suggestions:', err);
      addressSuggestionsSessionTokenRef.current = null;
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    } finally {
      if (requestSequence === addressSearchSequenceRef.current) {
        addressSearchAbortRef.current = null;
        setIsLoadingAddressSuggestions(false);
      }
    }
  }, [formState.city, formState.state]);

  // Handle address input change with debounced suggestions
  const handleAddressInputChange = useCallback((value: string) => {
    setFormState(prev => ({ ...prev, address: value }));

    if (addressSearchTimeoutRef.current) {
      clearTimeout(addressSearchTimeoutRef.current);
      addressSearchTimeoutRef.current = null;
    }
    addressSearchAbortRef.current?.abort();
    addressSearchAbortRef.current = null;
    addressDetailsAbortRef.current?.abort();
    addressDetailsAbortRef.current = null;
    addressSearchSequenceRef.current += 1;
    addressSuggestionsSessionTokenRef.current = null;
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
    setIsLoadingAddressSuggestions(false);

    if (value.trim().length < 3) {
      if (!value.trim()) placesSessionRef.current.reset();
      return;
    }

    addressSearchTimeoutRef.current = setTimeout(() => {
      fetchAddressSuggestions(value);
    }, 500);
  }, [fetchAddressSuggestions]);

  // Handle address suggestion selection
  const handleAddressSelect = useCallback(async (suggestion: AddressSuggestion) => {
    if (addressSearchTimeoutRef.current) {
      clearTimeout(addressSearchTimeoutRef.current);
      addressSearchTimeoutRef.current = null;
    }
    addressSearchAbortRef.current?.abort();
    addressSearchAbortRef.current = null;
    addressSearchSequenceRef.current += 1;
    setIsLoadingAddressSuggestions(false);
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);

    // Set immediate values from suggestion. The autocomplete response carries only
    // address + placeId, so pincode/latitude/longitude are left untouched here and
    // filled by the Place Details phase below. Blanking them eagerly used to wipe
    // manually entered coordinates whenever that follow-up request failed, and a
    // store saved without coordinates has an attendance geofence with no centre.
    setFormState(prev => ({
      ...prev,
      address: suggestion.address,
    }));

    if (!suggestion.placeId) {
      addressSuggestionsSessionTokenRef.current = null;
      placesSessionRef.current.reset();
      return;
    }

    const sessionToken =
      addressSuggestionsSessionTokenRef.current || placesSessionRef.current.getToken();
    addressSuggestionsSessionTokenRef.current = null;
    placesSessionRef.current.complete(sessionToken);
    addressDetailsAbortRef.current?.abort();
    const abortController = new AbortController();
    addressDetailsAbortRef.current = abortController;
    const selectionSequence = addressSearchSequenceRef.current;

    try {
      const response = await googleMapsAPI.placeDetails(
        suggestion.placeId,
        sessionToken,
        abortController.signal,
      );

      if (
        abortController.signal.aborted ||
        selectionSequence !== addressSearchSequenceRef.current ||
        response.data?.status !== 'success'
      ) {
        return;
      }
      const details = response.data.data;

      setFormState(prev => ({
        ...prev,
        address: details?.address || prev.address,
        pincode: details?.pincode || prev.pincode,
        latitude: details?.latitude || prev.latitude,
        longitude: details?.longitude || prev.longitude,
      }));
    } catch (err: unknown) {
      const cancelled =
        abortController.signal.aborted ||
        (typeof err === 'object' && err !== null && 'code' in err && err.code === 'ERR_CANCELED');
      if (cancelled) return;
      console.error('Failed to fetch address details:', err);
    } finally {
      if (addressDetailsAbortRef.current === abortController) {
        addressDetailsAbortRef.current = null;
      }
    }
  }, []);

  useEffect(() => () => {
    if (addressSearchTimeoutRef.current) clearTimeout(addressSearchTimeoutRef.current);
    addressSearchAbortRef.current?.abort();
    addressDetailsAbortRef.current?.abort();
    placesSessionRef.current.reset();
  }, []);

  // Fetch stores from API
  const fetchStores = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await storesAPI.getAll(selectedProjectId!);
      
      if (response.data.status === 'success') {
        const storesData = response.data.data || [];
        const transformedStores: StoreRecord[] = storesData.map(storeRecordOf);
        // Deduplicate stores by ID
        const uniqueStores = transformedStores.filter(
          (store, index, self) => index === self.findIndex(s => s.id === store.id)
        );
        setStores(uniqueStores);
      }
    } catch (err: any) {
      console.error('Failed to fetch stores:', err);
      setError(err.response?.data?.message || 'Failed to fetch stores');
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId]);

  // When "Deploy Employee" is pressed, hiring at that store is asked about BEFORE the deploy is
  // saved (product owner, 21 and 22 Oct 2026), from this page's own list of hiring once it is
  // read. The picker lists only people deployed nowhere, or roaming ones who keep their stores,
  // so no store is left. What its windows do shows on the rows as this page's own windows do:
  // a link at once from the answer and both lists read again; a hiring closed as filled reads
  // the stores again.
  const deployHiring = useDeployHiringCheck({
    projectId: selectedProjectId,
    userKey: hiringUserKey,
    onChanged: (link) => {
      if (link) storeHiring.apply(link);
      reloadHiringLists();
    },
    onPlaced: () => {
      void fetchStores();
    },
  });

  // Fetch categories from API
  const fetchCategories = useCallback(async () => {
    try {
      const response = await categoriesAPI.getAll(selectedProjectId!);
      if (response.data.status === 'success') {
        setCategories(response.data.data || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch categories:', err);
    }
  }, [selectedProjectId]);

  // Active custom field definitions for this project. Only the live ones — a
  // retired field must leave the store form while keeping its stored values.
  // Fails soft: custom fields are optional metadata and must never stop the store
  // form from opening.
  const fetchCustomFields = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      const response = await storeCustomFieldsAPI.list(selectedProjectId);
      setCustomFieldDefs(response.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch store custom fields:', err);
      setCustomFieldDefs([]);
    }
  }, [selectedProjectId]);

  // Active trades for this project. Fails soft to an empty list, which the
  // formTradeOptions fallback then covers with the trades already in the data.
  const fetchTrades = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      const response = await storeTradesAPI.list(selectedProjectId);
      setTradeOptions(((response.data?.data || []) as StoreTrade[]).map((t) => t.name));
    } catch (err) {
      console.error('Failed to fetch store trades:', err);
      setTradeOptions([]);
    }
  }, [selectedProjectId]);

  // Fetch states from API - single API call, no fallback needed
  const fetchLocations = useCallback(async () => {
    try {
      const response = await locationsAPI.getStates();
      if (response.data?.status === 'success' && Array.isArray(response.data.data)) {
        // Store full state objects to access stateId for cities lookup
        setApiStates(response.data.data);
        console.log('States fetched from API:', response.data.data.length);
      }
    } catch (err: any) {
      console.error('Failed to fetch states:', err.message);
    }
  }, []);

  // Fetch cities for a specific state from API using stateId
  const fetchCitiesForState = useCallback(async (stateName: string) => {
    if (!stateName) return;
    
    // Find the stateId from the apiStates array
    const stateData = apiStates.find(s => s.stateName === stateName);
    if (!stateData) {
      console.warn('State not found in API states:', stateName);
      return;
    }
    
    setIsLoadingCities(true);
    try {
      // Use stateId directly to fetch cities
      const response = await locationsAPI.getCitiesByStateId(stateData.stateId);
      if (response.data.status === 'success') {
        const citiesData = response.data.data || [];
        // Extract city names from the city objects (API returns {cityId, cityName, stateId,
        // stateName, latitude, longitude, tierId, tierName, regionId, regionName}).
        const cities: CityData[] = citiesData.map((c: any) => ({
          name: c.cityName || c.name || '',
          pincode: c.pincode || null,
          tierName: c.tierName || null
        }));
        setCitiesByState(prev => ({
          ...prev,
          [stateName]: cities
        }));
      }
    } catch (err: any) {
      console.error('Failed to fetch cities:', err);
    } finally {
      setIsLoadingCities(false);
    }
  }, [apiStates]);

  // Fetch undeployed employees for deploy modal
  const fetchUndeployedEmployees = useCallback(async () => {
    try {
      setIsLoadingEmployees(true);
      const response = await storesAPI.getUndeployedEmployees(selectedProjectId!, true);
      if (response.data.status === 'success') {
        const employees = response.data.data || [];
        const transformedEmployees: AvailableEmployee[] = employees.map((emp: any) => ({
          name: emp.name || '',
          code: emp.code || '',
          ctc: emp.monthlyCtc ? `₹${Number(emp.monthlyCtc).toLocaleString('en-IN')}` : '—',
          join: emp.dateOfJoining ? new Date(emp.dateOfJoining).toLocaleDateString('en-IN') : '—',
          designation: emp.designation || '',
          city: emp.city || '',
          state: emp.state || '',
          multiStore: emp.multiStore === true,
          leavingOn: emp.noticeLastWorkingDay || null,
        }));
        setAvailableEmployees(transformedEmployees);
      }
    } catch (err: any) {
      console.error('Failed to fetch undeployed employees:', err);
    } finally {
      setIsLoadingEmployees(false);
    }
  }, [selectedProjectId]);

  // Load data on mount
  useEffect(() => {
    fetchStores();
    fetchCategories();
    fetchLocations();
    fetchCustomFields();
    fetchTrades();
  }, [fetchStores, fetchCategories, fetchLocations, fetchCustomFields, fetchTrades]);

  // Reset the (project-scoped) intelligence cache when the project changes.
  useEffect(() => { setIntelligence(null); setIntelAttempted(false); }, [selectedProjectId]);

  // Lazily load the store-intelligence summary the FIRST time the Deployment filter
  // is used — it carries the ACTIVE headcount per store (exited employees excluded),
  // the same source the dashboard uses. The default store list load pays nothing for it.
  useEffect(() => {
    if (deploymentFilter === 'all' || !selectedProjectId || intelAttempted || intelLoading) return;
    const now = new Date();
    const periodMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    let cancelled = false;
    setIntelLoading(true);
    storeIntelligenceAPI.getSummary(selectedProjectId, periodMonth)
      .then((res) => { if (!cancelled) setIntelligence((res.data?.data ?? {}) as StoreIntel); })
      .catch(() => { if (!cancelled) setIntelligence(null); }) // null → degrade to roster count
      .finally(() => { if (!cancelled) { setIntelLoading(false); setIntelAttempted(true); } });
    return () => { cancelled = true; };
  }, [deploymentFilter, selectedProjectId, intelAttempted, intelLoading]);

  // Active deployed headcount for a store. Matches the dashboard: once intelligence is
  // loaded, a store ABSENT from it has 0 active employees (its roster is all exited);
  // before it loads (or on a load error) fall back to the roster count so the filter
  // still does something sensible.
  const activeHeadcountOf = (store: StoreRecord): number => {
    if (intelligence) {
      const emps = intelligence.stores?.[store.id]?.people?.employees;
      return Array.isArray(emps) ? emps.length : 0;
    }
    return store.employeeCount ?? store.employees?.length ?? 0;
  };

  // Who stands in each store, as the Deployed Employee / Reporting Manager columns list
  // them (active people, primary first, then by name), and the values the search box
  // matches them on. Worked out ONCE per store list, not per render or per keystroke.
  const peopleByStore = useMemo(() => {
    const byStore = new Map<string, readonly StorePerson[]>();
    for (const store of stores) byStore.set(store.id, storePeopleOf(store.employees));
    return byStore;
  }, [stores]);
  const peopleOfStore = useCallback(
    (store: StoreRecord): readonly StorePerson[] => peopleByStore.get(store.id) ?? NO_STORE_PEOPLE,
    [peopleByStore],
  );
  const peopleSearchByStore = useMemo(() => {
    const byStore = new Map<string, readonly string[]>();
    for (const [id, people] of peopleByStore) byStore.set(id, storePeopleSearchTerms(people));
    return byStore;
  }, [peopleByStore]);

  const filteredStores = useMemo(() => {
    return stores.filter((store) => {
      if (filters.trade && store.trade !== filters.trade) return false;
      if (filters.category && store.category !== filters.category) return false;
      if (filters.state && normalizeLocationKey(store.state) !== normalizeLocationKey(filters.state)) return false;
      if (filters.city && normalizeLocationKey(store.city) !== normalizeLocationKey(filters.city)) return false;
      if (filters.status && store.status !== filters.status) return false;
      if (filters.staffingStatus) {
        // Filter on the value the row RENDERS, or a store showing 'Hiring to be done'
        // would vanish when you filter for exactly that.
        const staffing = (store.staffingStatusEffective || store.staffingStatus || '').trim();
        // The sentinel matches "never set", which is the actionable case — a real
        // status can never equal it.
        if (filters.staffingStatus === STAFFING_UNSET ? staffing !== '' : staffing !== filters.staffingStatus) {
          return false;
        }
      }
      if (deploymentFilter === 'deployed' && activeHeadcountOf(store) === 0) return false;
      if (deploymentFilter === 'undeployed') {
        // A closed (Inactive) store is not a vacancy — the dashboard excludes it from
        // Undeployed Stores, so we do too. Otherwise: BELOW the category minimum =
        // undeployed — vacant (0) OR understaffed (e.g. an L3 store, min 2, with 1),
        // matching the dashboard's min-aware Undeployed Stores definition.
        if ((store.status ?? '').trim().toLowerCase() === 'inactive') return false;
        if (activeHeadcountOf(store) >= store.minEmployees) return false;
      }
      // The store's name or code, or the name or code of someone the table lists on it,
      // or of their reporting manager.
      const q = searchQuery.trim().toLowerCase();
      if (
        q
        && !store.name.toLowerCase().includes(q)
        && !store.storeCode.toLowerCase().includes(q)
        && !(peopleSearchByStore.get(store.id)?.some((term) => term.includes(q)) ?? false)
      ) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stores, filters, searchQuery, deploymentFilter, intelligence, peopleSearchByStore]);

  // Report the visible store count up to the tab shell so it can show "Stores (N)"
  // next to the heading — the count the standalone page used to show inline.
  useEffect(() => {
    onCount?.(filteredStores.length);
  }, [filteredStores.length, onCount]);

  // Column sorting — the same mechanism every report table uses. A first click sorts
  // A→Z (Last Updated: newest first), a second reverses it, a third restores the default
  // order. Each accessor returns what its cell RENDERS (the effective staffing status,
  // the region with its client-side fallback), so the order always agrees with what is
  // on screen. Blanks sink to the bottom in both directions. Memoised: this page
  // re-renders on every keystroke in its forms, and an inline map would re-sort the
  // whole roster each time.
  const storeSortAccessors = useMemo<SortAccessors<StoreRecord>>(() => ({
    trade: (s) => s.trade,
    name: (s) => s.name,
    storeCode: (s) => s.storeCode,
    // The cell's first line: the primary (or first) person, and that person's manager.
    deployedEmployee: (s) => {
      const first = peopleOfStore(s)[0];
      return first ? personHeadline(first.name, first.code) : null;
    },
    reportingManager: (s) => {
      const first = peopleOfStore(s)[0];
      return first ? personHeadline(first.managerName, first.managerCode) : null;
    },
    category: (s) => s.category,
    status: (s) => s.status,
    staffingStatus: (s) => s.staffingStatusEffective || s.staffingStatus,
    address: (s) => s.address,
    lastUpdated: (s) => s.updatedAtMs,
    state: (s) => s.state,
    region: (s) => s.region || regionForStore(s.state),
    city: (s) => s.city,
    tier: (s) => s.tier,
    pincode: (s) => s.pincode,
  }), [regionForStore, peopleOfStore]);
  const storeSort = useTableSort(filteredStores, storeSortAccessors, { firstDir: storeFirstSortDir });
  // What the table lists and the CSV exports, in the order on screen.
  const sortedStores = storeSort.sorted;

  // Filtered employees for deploy modal search
  const filteredEmployees = useMemo(() => {
    // A roaming promoter already on THIS store must not be offered again (the add would
    // 409) — they stay offered on every other store's picker.
    const onThisStore = new Set(deployedEmployees.map((e) => e.code.trim().toLowerCase()));
    const pool = availableEmployees.filter((emp) => !onThisStore.has(emp.code.trim().toLowerCase()));
    if (!employeeSearchTerm) return pool.slice(0, 20);
    const term = employeeSearchTerm.toLowerCase();
    return pool.filter(emp => 
      emp.name.toLowerCase().includes(term) || 
      emp.code.toLowerCase().includes(term) ||
      emp.city?.toLowerCase().includes(term)
    ).slice(0, 20);
  }, [availableEmployees, employeeSearchTerm, deployedEmployees]);

  const resetFilters = () => {
    setFilters({ trade: '', category: '', state: '', city: '', status: '', staffingStatus: '' });
    setSearchQuery('');
    setDeploymentFilter('all');
  };

  // Export the currently-visible stores together with their mapped employees as
  // CSV. One row per store↔employee pairing (stores with no employee mapped get a
  // single row with the employee columns blank) so the file carries the full
  // store list, employee list, and the mapping between them in one sheet. Honours
  // the active search + filters (exports exactly what the table shows).
  /**
   * One custom field value as a CSV cell. Multi-selects join with a pipe, matching
   * what the bulk-upload template asks for — so a downloaded file can be edited and
   * re-uploaded without reformatting. A comma would be ambiguous the moment an
   * option itself contains one.
   */
  const formatCustomFieldForCsv = (value: unknown): string => {
    if (value == null) return '';
    if (Array.isArray(value)) return value.join(' | ');
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  const handleDownloadStores = () => {
    const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    // Flatten newlines so a stray address value can't break a row. Quotes are left
    // alone: buildCsv already wraps and escapes every cell, so doubling them here
    // too would double-escape.
    const csvCell = (v: unknown): string => String(v ?? '').replace(/[\r\n]+/g, ' ');
    const headers = [
      // The first fifteen columns are the bulk upload template's first fifteen, IN THE
      // TEMPLATE'S ORDER (see downloadBulkUploadTemplate in the API). This file used to
      // list City before State while the template lists State before City, so pasting a
      // block of columns from one into the other reversed the two. Keep any column the
      // template does not have AFTER this block.
      'Store Code', 'Store Name', 'Trade', 'Store Category', 'Status', 'Staffing Status', 'State', 'City', 'Address',
      'Pincode', 'Latitude', 'Longitude', 'Attendance Radius (m)', 'Shift Start Time', 'Store Week Off',
      // Region and Tier are both derived from the 1Office masters and stored on the store row.
      'Region', 'Tier', 'Last Updated',
      // Who stands in the store, as the table's Deployed Employee and Reporting Manager
      // columns list them (active people, primary first, then by name), several in one
      // cell joined by "; " so the n-th name lines up with the n-th code and manager.
      // AFTER the template block (not after Store Code as on screen), so a block pasted
      // into the template still lands under the right headings. Named "Deployed Employee
      // Manager" so they never share a heading with the employee block's per-person
      // "Reporting Manager" / "Reporting Manager Code".
      'Deployed Employee', 'Deployed Employee Code', 'Deployed Employee Manager', 'Deployed Employee Manager Code',
      // Project-defined columns sit BEFORE the employee block: the employee columns
      // are position-matched against the ten blanks emitted for an unstaffed store,
      // and appending here would break that alignment.
      ...customFieldDefs.map((f) => f.label),
      // 'Designation' was REMOVED: store_employees.designation is written once at deploy
      // time from the 1Office budget name and never refreshed, so it was both mislabelled
      // and stale. 'Role' replaces it with the live ChannelPlex project role — the same
      // value the dashboard Role filter uses.
      'Employee Code', 'Employee Name', 'Role', 'Reporting Manager', 'Reporting Manager Code',
      'Date of Joining', 'Salary', 'iPro Status', 'Seed Score', 'Primary Employee',
    ];
    const rows: string[][] = [];
    for (const s of sortedStores) {
      const weekOff = s.storeWeekOff == null ? '' : (WEEKDAY_NAMES[s.storeWeekOff] ?? String(s.storeWeekOff));
      const people = storePeopleDownloadCells(peopleOfStore(s));
      const storeCols = [
        s.storeCode, s.name, s.trade, s.category, s.status, s.staffingStatus, s.state, s.city, s.address,
        s.pincode, s.latitude, s.longitude, s.attendanceRadius ?? '', s.shiftStartTime ?? '', weekOff,
        s.region || regionForStore(s.state) || '', s.tier || '', s.lastUpdated,
        people.employee, people.employeeCode, people.manager, people.managerCode,
        ...customFieldDefs.map((f) => formatCustomFieldForCsv(s.customFields?.[f.id])),
      ];
      const emps = Array.isArray(s.employees) ? s.employees : [];
      if (emps.length === 0) {
        // Ten blanks — must stay in lockstep with the employee columns in `headers`.
        rows.push([...storeCols, '', '', '', '', '', '', '', '', '', ''].map(csvCell));
      } else {
        for (const e of emps) {
          rows.push([
            ...storeCols,
            e.employee_code ?? '', e.employee_name ?? '',
            e.role_name ?? '', e.reporting_manager_name ?? '', e.reporting_manager_code ?? '',
            e.date_of_joining ?? '',
            e.salary ?? '', e.ipro_status ?? '', e.seed_score ?? '', e.is_primary ? 'Yes' : 'No',
          ].map(csvCell));
        }
      }
    }
    const date = new Date().toISOString().slice(0, 10);
    // With the byte-order mark: the Status column can read "Outside Approved – Redeploy",
    // and Excel shows that dash garbled in a CSV that does not declare itself UTF-8.
    downloadCsv(`stores_with_employees_${date}.csv`, CSV_UTF8_BOM + buildCsv(headers, rows));
  };

  const hasStoreFiltersApplied = !!(searchQuery || filters.trade || filters.category || filters.state || filters.city || filters.status || filters.staffingStatus);
  const storeMoreFiltersActiveCount = [filters.trade, filters.category, filters.state, filters.city].filter(Boolean).length;

  const changeFilter = (field: keyof typeof filters, value: string) => {
    if (field === 'state') {
      // When state changes, check if current city is valid for new state (match case-insensitively)
      const allCities = value ? buildCityList(value) : stores.map(s => s.city).filter(Boolean);

      setFilters((prev) => ({
        ...prev,
        state: value,
        // Reset city if it's not in the new state's cities
        city: allCities.some(c => normalizeLocationKey(c) === normalizeLocationKey(prev.city)) ? prev.city : ''
      }));
    } else {
      setFilters((prev) => ({ ...prev, [field]: value }));
    }
  };

  const openCreateModal = () => {
    resetAddressAutocomplete();
    setModalMode('create');
    setEditingStoreId(null);
    editOpenedStatusRef.current = null;
    setFormError(null);
    setFormState({
      trade: '',
      name: '',
      storeCode: '',
      categoryId: '',
      status: 'Active',
      // A store being created has nobody deployed yet — the same reasoning the
      // one-time backfill uses for a store with no deployment rows.
      staffingStatus: 'Hiring to be done',
      address: '',
      state: '',
      city: '',
      pincode: '',
      latitude: '',
      longitude: '',
      attendanceRadius: '100',
      shiftStartTime: '',
      storeWeekOff: '',
      compliancePolicy: '',
    });
    setCustomFieldValues({});
    setIsModalOpen(true);
  };

  const openEditModal = (store: StoreRecord) => {
    resetAddressAutocomplete();
    setModalMode('edit');
    setEditingStoreId(store.id);
    editOpenedStatusRef.current = statusBaselineOf(store);
    setFormError(null);
    // Map a dirty saved state (e.g. "PUNJAB") onto the canonical master spelling so the
    // dropdown pre-selects correctly and the full city list resolves. Saving then writes
    // the clean value back, so records self-heal as they are edited.
    const canonicalState = canonicalizeState(store.state);
    setFormState({
      trade: store.trade,
      name: store.name,
      storeCode: store.storeCode,
      categoryId: store.categoryId?.toString() || '',
      status: store.status,
      staffingStatus: store.staffingStatus || '',
      address: store.address,
      state: canonicalState,
      city: store.city,
      pincode: store.pincode,
      latitude: store.latitude,
      longitude: store.longitude,
      attendanceRadius: store.attendanceRadius?.toString() || '100',
      shiftStartTime: store.shiftStartTime || '',
      storeWeekOff: store.storeWeekOff != null ? String(store.storeWeekOff) : '',
      compliancePolicy: store.compliancePolicy || '',
    });
    // Must hydrate here, not just reset in openCreateModal — without this the form
    // opens blank and saving writes those blanks back over the stored values.
    setCustomFieldValues(store.customFields ? { ...store.customFields } : {});
    setIsModalOpen(true);
    // Load the canonical city list so the user can change city on edit.
    if (canonicalState) fetchCitiesForState(canonicalState);
  };

  const closeStoreModal = () => {
    resetAddressAutocomplete();
    setIsModalOpen(false);
  };

  const handleFormChange = (field: keyof typeof formState, value: string) => {
    if (field === 'state') {
      resetAddressAutocomplete();
      // When state changes, fetch cities for the new state
      if (value) {
        fetchCitiesForState(value);
      }
      
      // Check if current city is valid for new state (match case-insensitively)
      const allCities = value ? buildCityList(value) : [];

      setFormState((prev) => {
        const cityStillValid = allCities.some(c => normalizeLocationKey(c) === normalizeLocationKey(prev.city));
        return {
          ...prev,
          state: value,
          // Reset city, pincode, and coordinates if city is not in the new state's cities
          city: cityStillValid ? prev.city : '',
          pincode: cityStillValid ? prev.pincode : '',
          latitude: cityStillValid ? prev.latitude : '',
          longitude: cityStillValid ? prev.longitude : ''
        };
      });
    } else if (field === 'city') {
      resetAddressAutocomplete();
      // When city changes, just update the city and clear address/location fields
      // Pincode, lat, lng will be fetched when user searches/selects an address
      setFormState((prev) => ({
        ...prev,
        city: value,
        address: '', // Clear address when city changes
        pincode: '',
        latitude: '',
        longitude: ''
      }));
    } else {
      setFormState((prev) => ({ ...prev, [field]: value }));
    }
  };

  /** How hiring is offered for a store just added, as this person may raise or ask now; null = not at all. */
  const addedStoreMode = (store: StoreRecord): NewStoreRaiseMode | null => newStoreRaiseMode({
    canRaise: canRaiseHiring,
    canAsk: canAskHiring,
    storeStatus: store.status,
    staffingStatus: store.staffingStatusEffective || store.staffingStatus,
  });

  // What the "Store added" step offers: drawn only while it still holds, in the project the
  // store was added in. Null leaves only Done.
  const storeAddedMode = storeAdded && storeAdded.projectId === selectedProjectId ? addedStoreMode(storeAdded.store) : null;

  /** "Raise hiring for it" / "Ask for hiring for it": the step closes, and the Raise window opens for the store with New store. */
  const raiseForAddedStore = (store: StoreRecord, mode: NewStoreRaiseMode) => {
    setStoreAdded(null);
    setNewStoreToRaise(store);
    if (mode === 'raise') setRaiseHiringStore(store);
    else setAskHiringStore(store);
  };

  const handleStoreSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);

    if (!formState.name.trim() || !formState.storeCode.trim()) {
      setFormError('Store name and Store Code are required.');
      return;
    }

    // A new store needs its address and PIN code: hiring is raised for a store with
    // both, and the server refuses a new store without them. Edit is not held to it, so
    // an older store with neither can still be edited for something else.
    if (modalMode === 'create' && !formState.address.trim()) {
      setFormError('Address is required.');
      return;
    }
    if (modalMode === 'create' && !newStorePinCode(formState.pincode)) {
      setFormError('PIN code must be 6 digits, not starting with 0.');
      return;
    }

    // A store's attendance geofence is centred on its coordinates, so saving a blank
    // pair silently breaks sign-in for that store. Required on create; on edit only
    // the pair's consistency is enforced, so pre-existing rows that already have no
    // coordinates can still be edited for unrelated reasons.
    const hasLatitude = (formState.latitude || '').trim() !== '';
    const hasLongitude = (formState.longitude || '').trim() !== '';
    if (hasLatitude !== hasLongitude) {
      setFormError('Latitude and Longitude must both be filled, or both left blank.');
      return;
    }
    if (modalMode === 'create' && !hasLatitude) {
      setFormError('Latitude and Longitude are required. Select an address from the suggestions, or enter the coordinates manually.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        store_name: formState.name.trim(),
        store_code: formState.storeCode.trim(),
        trade: formState.trade,
        store_category: formState.categoryId ? parseInt(formState.categoryId) : null,
        // A new store always states its status. An existing one only when the user
        // changed it: the form opens on the status the list had loaded, and posting
        // that back untouched would let a rename or an address fix overwrite a status
        // that has moved since — or be refused outright, when the store was awaiting
        // redeployment and its last employee has been moved out meanwhile.
        ...(modalMode === 'create' || formState.status !== editOpenedStatusRef.current
          ? { store_status: formState.status }
          : {}),
        staffing_status: formState.staffingStatus,
        address: formState.address,
        state: formState.state,
        city: formState.city,
        pin_code: modalMode === 'create' ? newStorePinCode(formState.pincode) : formState.pincode,
        latitude: formState.latitude,
        longitude: formState.longitude,
        attendance_radius: formState.attendanceRadius ? parseInt(formState.attendanceRadius) : 100,
        shift_start_time: formState.shiftStartTime || null,
        store_week_off: formState.storeWeekOff === '' ? null : Number(formState.storeWeekOff),
        compliance_policy: formState.compliancePolicy.trim() || null,
        custom_fields: customFieldValues,
      };

      // The store a create made, when it made a NEW one: 201. A save over a store code the
      // project already has answers 200, and is never offered hiring as a new store.
      let added: StoreRecord | null = null;
      if (modalMode === 'create') {
        const res = await storesAPI.create({ ...payload, project_id: selectedProjectId });
        added = res?.status === 201 && res.data?.data?.id ? storeRecordOf(res.data.data) : null;
      } else if (editingStoreId) {
        await storesAPI.update(editingStoreId, payload);
      }

      closeStoreModal();
      // Hiring may be raised or asked for here, and the store is Active with no staffing
      // hold: the window turns into the "Store added" step. Otherwise it closes, as before.
      if (added && selectedProjectId && addedStoreMode(added) !== null) {
        setStoreAdded({ store: added, projectId: selectedProjectId });
      }
      fetchStores();
    } catch (err: any) {
      console.error('Failed to save store:', err);
      setFormError(err.response?.data?.message || 'Failed to save store');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeployEmployee = async () => {
    if (!selectedCandidateCode || !deployModalStore) return;
    
    const selectedEmployee = availableEmployees.find((emp) => emp.code === selectedCandidateCode);
    if (!selectedEmployee) return;

    const storeId = deployModalStore.id;
    const storeName = deployModalStore.name;
    setIsSubmitting(true);
    setDeployModalError(null);
    // Hiring at the store is asked about before anything is saved (product owner, 22 Oct 2026),
    // from this page's own list of hiring once it is read (no request). With nothing to ask the
    // deploy goes on at once. Still short with them on it: as the store's people stand now.
    const hiring = await deployHiring.before({
      joined: [{
        storeId,
        storeName,
        personName: selectedEmployee.name,
        short: deployedEmployees.filter((e) => e.isActive).length + 1 < deployModalStore.minEmployees,
      }],
      known: storeHiring.status === 'ready'
        ? { byStore: storeHiring.byStore, raise: storeHiring.raise, cancel: storeHiring.cancel }
        : null,
    });
    if (!hiring.deploy) {
      // "Don’t deploy": nothing is saved, and the deploy window is as it was.
      setIsSubmitting(false);
      return;
    }
    let saved = false;
    try {
      if (selectedEmployee.multiStore) {
        // ADD this store and keep the ones they already hold. The swap path would also
        // add (deployEmployeeToStore honours the role) but forces is_primary=true, which
        // would leave a staffed store with two primaries.
        await storesAPI.addEmployeeStore(selectedEmployee.code, {
          storeId: deployModalStore.id,
          employee_name: selectedEmployee.name,
          designation: selectedEmployee.designation,
          date_of_joining: selectedEmployee.join,
          salary: selectedEmployee.ctc ? parseInt(selectedEmployee.ctc.replace(/[^\d]/g, ''), 10) || undefined : undefined,
        });
      } else {
        await storesAPI.swap({
          currentStoreId: deployModalStore.id,
          newEmployeeCode: selectedEmployee.code,
          newEmployeeName: selectedEmployee.name,
          newEmployeeDesignation: selectedEmployee.designation,
          newEmployeeDateOfJoining: selectedEmployee.join,
          newEmployeeSalary: selectedEmployee.ctc ? parseInt(selectedEmployee.ctc, 10) : undefined,
        });
      }
      saved = true;
      
      // Only if this is still the modal the deploy was started from: it may have been
      // closed, or opened on another store, while the request was out.
      if (deployModalOpenForRef.current === storeId) {
        setSelectedCandidateCode(null);
        closeDeployModal();
      }
      fetchStores();
    } catch (err: any) {
      console.error('Failed to deploy employee:', err);
      if (deployModalOpenForRef.current === storeId) {
        setDeployModalError(err.response?.data?.message || 'Failed to deploy employee');
      }
    } finally {
      setIsSubmitting(false);
      // Saved: what was chosen for its hiring is done now. Not saved: nothing is done to hiring.
      if (saved) hiring.saved();
      else hiring.failed();
    }
  };

  // Removes ONE employee, named by code. A request without the code is read by the API
  // as "everyone at this store" — which is what this used to send, so Remove on one
  // person's row unmapped every employee of a shared store.
  const handleRemoveEmployee = async (storeId: string, employeeCode: string) => {
    // Is the modal this was clicked in still the one on screen? It can be closed, or
    // opened again on another store, while the request is out, and the answer must then
    // not touch that other store's list, question or message.
    const stillOpen = () => deployModalOpenForRef.current === storeId;
    setIsSubmitting(true);
    setDeployModalError(null);
    try {
      await storesAPI.undeploy(storeId, employeeCode);
      fetchStores();
      // Refresh the deployed employees in modal
      if (stillOpen()) {
        setDeployedEmployees(prev => prev.filter(e => e.code !== employeeCode));
        // A fresh read still out may have been answered before this removal.
        removedSinceReadRef.current.add(employeeCodeKey(employeeCode));
      }
    } catch (err: any) {
      console.error('Failed to remove employee:', err);
      if (stillOpen()) {
        setDeployModalError(err.response?.data?.message || 'Failed to remove employee');
        refocusRemoveRef.current = employeeCode;
      }
    } finally {
      if (stillOpen()) setRemoveConfirmCode(null);
      setIsSubmitting(false);
    }
  };

  const fetchTimeline = useCallback(async (storeId: string) => {
    const reqId = ++timelineReqRef.current;
    setTimelineLoading(true);
    setTimelineError(null);
    try {
      const response = await storesAPI.getTimeline(storeId);
      if (reqId !== timelineReqRef.current) return;
      setTimeline(response.data?.data?.entries || []);
    } catch (err: any) {
      if (reqId !== timelineReqRef.current) return;
      console.error('Failed to load store timeline:', err);
      setTimelineError(err.response?.data?.message || 'Could not load the timeline for this store.');
      setTimeline([]);
    } finally {
      if (reqId === timelineReqRef.current) setTimelineLoading(false);
    }
  }, []);

  /** Enabled only when the save would actually record something. */
  const statusModalDirty = !!statusModalStore && (
    statusFormState !== statusBaselineOf(statusModalStore) ||
    staffingFormState !== (statusModalStore.staffingStatus || '') ||
    statusRemark.trim() !== ''
  );

  const handleStatusUpdate = async () => {
    if (!statusModalStore || !statusFormState || !statusModalDirty) return;

    setIsSubmitting(true);
    setFormError(null);
    try {
      // Each status goes out only when the user changed it, for the reason given in
      // handleStoreSubmit: this dialog also opens on what the list had loaded.
      const statusChanged = statusFormState !== statusBaselineOf(statusModalStore);
      const staffingChanged = staffingFormState !== (statusModalStore.staffingStatus || '');
      // A remark on its own still has to name one store field — updateStore answers
      // "No valid fields to update" to a body with none and the note is never written.
      // The untouched staffing status is the one that is harmless to repeat.
      const remarkOnly = !statusChanged && !staffingChanged;
      await storesAPI.update(statusModalStore.id, {
        ...(statusChanged ? { store_status: statusFormState } : {}),
        ...(staffingChanged || remarkOnly ? { staffing_status: staffingFormState } : {}),
        // Not a store column — updateStore reads it as the timeline note.
        remark: statusRemark.trim(),
      });
      // Stay open and refresh in place: the point of this modal is now the
      // timeline, and closing it would hide the entry just written.
      const updated = {
        ...statusModalStore,
        status: statusFormState,
        // What the column now holds: the redeploy status is stored as Active.
        storedStatus: statusFormState === STORE_STATUS_REDEPLOY ? STORE_STATUS.ACTIVE : statusFormState,
        staffingStatus: staffingFormState,
      };
      setStatusModalStore(updated);
      setStatusRemark('');
      await Promise.all([fetchTimeline(statusModalStore.id), fetchStores()]);
    } catch (err: any) {
      console.error('Failed to update status:', err);
      setFormError(err.response?.data?.message || 'Failed to update status');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeDeployModal = () => {
    deployModalOpenForRef.current = null;
    setIsDeployModalOpen(false);
  };

  const openDeployModal = (store: StoreRecord) => {
    deployModalOpenForRef.current = store.id;
    setDeployModalStore(store);
    setSelectedCandidateCode(null);
    setEmployeeSearchTerm('');
    setFormError(null);
    setDeployModalError(null);
    setRemoveConfirmCode(null);
    refocusRemoveRef.current = null;
    
    // The row's copy first, from employees array (store_employees junction table): shown while
    // the store is read afresh below, and kept if that read fails.
    let rowCopy: DeployedEmployee[] = [];
    if (Array.isArray(store.employees) && store.employees.length > 0) {
      rowCopy = deployedEmployeesOf(store.employees);
    } else if (store.employeeCode && store.employeeName) {
      // Fallback to single employee fields for backward compatibility
      rowCopy = [{
        name: store.employeeName,
        code: store.employeeCode,
        role: store.designation || 'Full Time',
        salary: store.salary ? `₹${store.salary.toLocaleString('en-IN')}/mo` : '—',
        joined: store.dateOfJoining ? new Date(store.dateOfJoining).toLocaleDateString('en-IN') : '—',
        isActive: true, // legacy single-employee shape carries no roster flag
      }];
    }
    setDeployedEmployees(rowCopy);
    readDeployedAfresh(store.id, rowCopy);

    fetchUndeployedEmployees();
    setIsDeployModalOpen(true);
  };

  /**
   * The store's people, read afresh each time the Deploy Employee modal opens (product owner,
   * 22 Oct 2026: re-opened at once after a deploy, it said "Currently Deployed Employees (0)",
   * "Updated just now", and offered the person again). The page's row is read again in the
   * background after a deploy, so a quick re-open showed the row as it was before it. This read
   * sets the list, its count and the picker's leave-out. GET /stores/:id, as the hiring question
   * reads a store: it leaves out salaries for a person without the money grant, as GET /stores
   * does (GET /stores/:id/employees does not). It carries no roster flag, so the row copy's is
   * kept for each person. An answer for a modal since closed, opened on another store, or opened
   * again is dropped; a failed read keeps the row copy, with no message.
   */
  const readDeployedAfresh = (storeId: string, rowCopy: readonly DeployedEmployee[]) => {
    const seq = ++deployedReadSeqRef.current;
    const current = () => seq === deployedReadSeqRef.current && deployModalOpenForRef.current === storeId;
    removedSinceReadRef.current = new Set();
    setDeployedRead('checking');
    const rowActive = new Map(rowCopy.map((e) => [employeeCodeKey(e.code), e.isActive]));
    // Inside the chain: a throw while building the request is a failed read, never the page's.
    Promise.resolve()
      .then(() => storesAPI.getById(storeId))
      .then((res) => {
        if (!current()) return;
        const row = res?.data?.data as { id?: unknown; employees?: unknown } | undefined;
        if (!row || String(row.id ?? '') !== storeId || !Array.isArray(row.employees)) {
          setDeployedRead('row');
          return;
        }
        const removed = removedSinceReadRef.current;
        setDeployedEmployees(
          deployedEmployeesOf(row.employees as StoreEmployeeRow[], (code) => rowActive.get(employeeCodeKey(code)))
            .filter((e) => !removed.has(employeeCodeKey(e.code))),
        );
        setDeployedRead('fresh');
      })
      .catch(() => {
        if (current()) setDeployedRead('row');
      });
  };

  const currency = (value: number) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(value);

  const headerActions = (
    <>
      <button
        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={handleDownloadStores}
        disabled={filteredStores.length === 0}
        title="Download the visible stores with their mapped employees as CSV"
      >
        <Download className="h-4 w-4" />
        Download
      </button>
      <button
        className="inline-flex items-center gap-2 rounded-lg border border-primary-600 bg-white px-4 py-2 text-sm font-semibold text-primary-600 shadow-sm transition-colors hover:bg-primary-50"
        onClick={() => setIsBulkUploadModalOpen(true)}
      >
        <Upload className="h-4 w-4" />
        Bulk Upload
      </button>
      <button
        className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
        onClick={openCreateModal}
      >
        Add New Store
      </button>
    </>
  );

  return (
    <div className="space-y-6">
      {/* Embedded, the shell owns the heading and the actions sit in its tab row,
          so the two share one line. The store count is not repeated up there —
          the filter bar's "Showing X of Y" already carries it. */}
      {embedded ? (
        actionSlot && createPortal(headerActions, actionSlot)
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Stores{' '}
              <span className="text-base font-medium text-gray-400">({filteredStores.length})</span>
            </h1>
          </div>
          <div className="flex items-center gap-3">{headerActions}</div>
        </div>
      )}

      <div className="rounded-lg border border-gray-100 bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search store, code, employee or manager…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <select
            value={filters.status}
            onChange={(e) => changeFilter('status', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Statuses</option>
            {filtersConfig.status.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>

          {/* Staffing status — the manpower-pipeline axis. Separate from Status
              above on purpose: a store is routinely Active AND still hiring. */}
          <select
            value={filters.staffingStatus}
            onChange={(e) => changeFilter('staffingStatus', e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            title="Filter by where the store sits in the manpower pipeline"
          >
            <option value="">All Staffing</option>
            {filtersConfig.staffingStatus.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
            <option value={STAFFING_UNSET}>— Not set —</option>
          </select>

          {/* Deployment: matches the dashboard's Deployment tab. "Undeployed" =
              an open store with no ACTIVE employee, so its count reconciles with the
              dashboard's Undeployed Stores card (exited-only stores counted, closed
              stores excluded). */}
          <select
            value={deploymentFilter}
            onChange={(e) => setDeploymentFilter(e.target.value as DeploymentFilter)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            title="Filter by whether the store has an active employee deployed (matches the dashboard)"
          >
            <option value="all">All Deployment</option>
            <option value="deployed">Deployed</option>
            <option value="undeployed">Undeployed{intelLoading ? ' (loading…)' : ''}</option>
          </select>

          <MoreFiltersDropdown
            activeCount={storeMoreFiltersActiveCount}
            open={showMoreFilters}
            onToggle={() => setShowMoreFilters((p) => !p)}
            onClose={() => setShowMoreFilters(false)}
          >
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Trade</label>
                    <select
                      value={filters.trade}
                      onChange={(e) => changeFilter('trade', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">All Trades</option>
                      {filtersConfig.trade.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                    <select
                      value={filters.category}
                      onChange={(e) => changeFilter('category', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">All Categories</option>
                      {filtersConfig.category.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
                    <select
                      value={filters.state}
                      onChange={(e) => changeFilter('state', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">All States</option>
                      {filtersConfig.state.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">City</label>
                    <select
                      value={filters.city}
                      onChange={(e) => changeFilter('city', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">All Cities</option>
                      {availableCities.map((city) => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
          </MoreFiltersDropdown>

          {hasStoreFiltersApplied && (
            <button
              type="button"
              onClick={resetFilters}
              className="text-sm text-gray-600 hover:text-gray-900 underline"
            >
              Clear all
            </button>
          )}

          <div className="ml-auto text-xs text-gray-500 whitespace-nowrap">
            Showing {filteredStores.length} of {stores.length}
          </div>
        </div>

        {hasStoreFiltersApplied && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {searchQuery && (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 border border-blue-200">
                Search: <span className="font-semibold">{searchQuery}</span>
                <button type="button" onClick={() => setSearchQuery('')} className="ml-1 text-blue-600 hover:text-blue-800">×</button>
              </span>
            )}
            {filters.status && (
              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 border border-indigo-200">
                Status: <span className="font-semibold">{filters.status}</span>
                <button type="button" onClick={() => changeFilter('status', '')} className="ml-1 text-indigo-600 hover:text-indigo-800">×</button>
              </span>
            )}
            {filters.staffingStatus && (
              <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2.5 py-1 text-xs font-medium text-violet-700 border border-violet-200">
                Staffing: <span className="font-semibold">{filters.staffingStatus === STAFFING_UNSET ? 'Not set' : filters.staffingStatus}</span>
                <button type="button" onClick={() => changeFilter('staffingStatus', '')} className="ml-1 text-violet-600 hover:text-violet-800">×</button>
              </span>
            )}
            {filters.trade && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 border border-amber-200">
                Trade: <span className="font-semibold">{filters.trade}</span>
                <button type="button" onClick={() => changeFilter('trade', '')} className="ml-1 text-amber-600 hover:text-amber-800">×</button>
              </span>
            )}
            {filters.category && (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 border border-green-200">
                Category: <span className="font-semibold">{filters.category}</span>
                <button type="button" onClick={() => changeFilter('category', '')} className="ml-1 text-green-600 hover:text-green-800">×</button>
              </span>
            )}
            {filters.state && (
              <span className="inline-flex items-center gap-1 rounded-full bg-pink-50 px-2.5 py-1 text-xs font-medium text-pink-700 border border-pink-200">
                State: <span className="font-semibold">{filters.state}</span>
                <button type="button" onClick={() => changeFilter('state', '')} className="ml-1 text-pink-600 hover:text-pink-800">×</button>
              </span>
            )}
            {filters.city && (
              <span className="inline-flex items-center gap-1 rounded-full bg-cyan-50 px-2.5 py-1 text-xs font-medium text-cyan-700 border border-cyan-200">
                City: <span className="font-semibold">{filters.city}</span>
                <button type="button" onClick={() => changeFilter('city', '')} className="ml-1 text-cyan-600 hover:text-cyan-800">×</button>
              </span>
            )}
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
              <tr>
                <SortTh label="Trade" sortKey="trade" sort={storeSort} className="whitespace-nowrap px-6 py-4" />
                <SortTh label="Store Name" sortKey="name" sort={storeSort} className="whitespace-nowrap px-6 py-4" />
                <SortTh label="Store Code" sortKey="storeCode" sort={storeSort} className="whitespace-nowrap px-6 py-4" />
                <SortTh label="Deployed Employee" sortKey="deployedEmployee" sort={storeSort} className="whitespace-nowrap px-6 py-4" />
                <SortTh label="Reporting Manager" sortKey="reportingManager" sort={storeSort} className="whitespace-nowrap px-6 py-4" />
                <SortTh label="Store Category" sortKey="category" sort={storeSort} className="whitespace-nowrap px-6 py-4" />
                <SortTh label="Status" sortKey="status" sort={storeSort} className="whitespace-nowrap px-6 py-4" />
                <SortTh label="Staffing Status" sortKey="staffingStatus" sort={storeSort} className="whitespace-nowrap px-6 py-4" />
                {/* The Hiring header and its two cells below are written here, under the
                    same `hiringOn &&`, so the column count of every row moves together. Hiring and
                    Actions are a little narrower than the other columns (px-3), so at a laptop's
                    width the sticky Actions column, six controls wide, does not cover the Hiring word. */}
                {hiringOn && <th className="whitespace-nowrap px-3 py-4">Hiring</th>}
                <SortTh label="Address" sortKey="address" sort={storeSort} className="whitespace-nowrap px-6 py-4" />
                <SortTh label="Last Updated" sortKey="lastUpdated" sort={storeSort} className="whitespace-nowrap px-6 py-4" />
                <SortTh label="State" sortKey="state" sort={storeSort} className="whitespace-nowrap px-6 py-4" />
                <SortTh label="Region" sortKey="region" sort={storeSort} className="whitespace-nowrap px-6 py-4" />
                <SortTh label="City" sortKey="city" sort={storeSort} className="whitespace-nowrap px-6 py-4" />
                <SortTh label="Tier" sortKey="tier" sort={storeSort} className="whitespace-nowrap px-6 py-4" />
                <SortTh label="PIN Code" sortKey="pincode" sort={storeSort} className="whitespace-nowrap px-6 py-4" />
                <th className="sticky right-0 z-10 whitespace-nowrap bg-gray-100 px-3 py-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white text-left">
              {loading ? (
                <>
                  {Array.from({ length: 8 }).map((_, index) => (
                    <tr key={index}>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-16" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-40" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-24" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-32" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-32" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-20" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-6 w-20 rounded-full" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-6 w-28 rounded-full" /></td>
                      {hiringOn && <td className="whitespace-nowrap px-3 py-4"><Skeleton className="h-6 w-28 rounded-full" /></td>}
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-48" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-24" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-28" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-20" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-24" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-16" /></td>
                      <td className="whitespace-nowrap px-6 py-4"><Skeleton className="h-4 w-20" /></td>
                      <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-3 py-4 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]">
                        <div className="flex items-center justify-center gap-2">
                          <Skeleton className="h-8 w-8 rounded-lg" />
                          <Skeleton className="h-8 w-8 rounded-lg" />
                          <Skeleton className="h-8 w-8 rounded-lg" />
                          {canManageHiring && <Skeleton className="h-8 w-8 rounded-lg" />}
                          {canRaiseHiring && <Skeleton className="h-8 w-8 rounded-lg" />}
                          {canAskHiring && <Skeleton className="h-8 w-8 rounded-lg" />}
                        </div>
                      </td>
                    </tr>
                  ))}
                </>
              ) : error ? (
                <tr>
                  <td colSpan={hiringOn ? 17 : 16} className="px-6 py-12 text-center">
                    <AlertCircle className="mx-auto h-8 w-8 text-red-500" />
                    <p className="mt-2 text-sm text-red-500">{error}</p>
                    <button
                      onClick={fetchStores}
                      className="mt-2 text-sm text-primary-600 hover:underline"
                    >
                      Try again
                    </button>
                  </td>
                </tr>
              ) : filteredStores.length === 0 ? (
                <tr>
                  <td colSpan={hiringOn ? 17 : 16} className="px-6 py-12 text-center">
                    <p className="text-sm text-gray-500">
                      {stores.length === 0
                        ? 'No stores found. Click "Add New Store" to create one.'
                        : 'No stores match your filters.'}
                    </p>
                    {Object.values(filters).some(Boolean) && (
                      <button
                        onClick={resetFilters}
                        className="mt-2 text-sm text-primary-600 hover:underline"
                      >
                        Clear filters
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                sortedStores.map((store) => (
                  <tr key={store.id} className="group hover:bg-gray-50">
                    <td className="whitespace-nowrap px-6 py-4 text-left text-gray-900">{store.trade}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-left text-gray-900">{store.name}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-left text-gray-900">{store.storeCode || '-'}</td>
                    {/* Each active person on their own line, their manager on the same line of the next column. */}
                    <DeployedEmployeeCell people={peopleOfStore(store)} />
                    <ReportingManagerCell people={peopleOfStore(store)} />
                    <td className="whitespace-nowrap px-6 py-4 text-left text-gray-900">{store.category || 'N/A'}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-left">
                      <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${
                        STORE_STATUS_BADGE[store.status] ?? STORE_STATUS_OTHER_BADGE
                      }`}>
                        {store.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-left">
                      {(store.staffingStatusEffective || store.staffingStatus) ? (
                        <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${
                          STAFFING_STATUS_BADGE[store.staffingStatusEffective || store.staffingStatus] ?? STAFFING_STATUS_UNSET_BADGE
                        }`}>
                          {store.staffingStatusEffective || store.staffingStatus}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Not set</span>
                      )}
                    </td>
                    {/* One dot and one word, the Hiring dashboard's four statuses (15 Oct 2026).
                        A dash when nothing is followed - and when the list could not be read. */}
                    {hiringOn && (
                      <td className="whitespace-nowrap px-3 py-4 text-left">
                        {storeHiring.status === 'ready' ? (
                          <StoreHiringDot
                            storeName={store.name}
                            links={storeHiring.byStore.get(storeHiringKey(store.id)) ?? NO_HIRING_LINKS}
                            // The store's hiring request that waits for confirmation, for those shown them.
                            request={requestsShown ? hiringRequests.byStore.get(storeHiringKey(store.id)) ?? null : null}
                            onOpen={() => setHiringPanelStore(store)}
                          />
                        ) : storeHiring.status === 'failed' ? (
                          <span className="text-xs text-gray-400">—</span>
                        ) : (
                          <Skeleton className="h-6 w-28 rounded-full" />
                        )}
                      </td>
                    )}
                    <td className="whitespace-nowrap px-6 py-4 text-left text-gray-900">{store.address || '-'}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-left text-gray-600">{store.lastUpdated}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-left text-gray-900">{store.state || '-'}</td>
                    {/* Prefers the value resolved server-side; falls back to resolving from
                        the state master here so the column is populated even before the
                        backfill has run. "-" means the state has no 1Office master match. */}
                    <td className="whitespace-nowrap px-6 py-4 text-left text-gray-900">{store.region || regionForStore(store.state) || '-'}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-left text-gray-900">{store.city || '-'}</td>
                    {/* Tier has no client-side fallback: it comes from the per-state city
                        master, which this page only ever fetches for the ONE state selected
                        in the form. "-" until the backfill populates it. */}
                    <td className="whitespace-nowrap px-6 py-4 text-left text-gray-900">{store.tier || '-'}</td>
                    <td className="whitespace-nowrap px-6 py-4 text-left text-gray-900">{store.pincode || '-'}</td>
                    <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-3 py-4 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] group-hover:bg-gray-50">
                      <TableActionGroup>
                        <TableActionButton action="edit" label={`Edit ${store.name}`}
                          onClick={() => openEditModal(store)} />
                        <TableActionButton action="assign" label={`Assign team for ${store.name}`}
                          onClick={() => openDeployModal(store)} />
                        <TableActionButton action="settings" label={`Change status for ${store.name}`}
                          onClick={() => {
                            setStatusModalStore(store);
                            setStatusFormState(store.status);
                            setStaffingFormState(store.staffingStatus || '');
                            setStatusRemark('');
                            setFormError(null);
                            setTimeline([]);
                            setIsStatusModalOpen(true);
                            fetchTimeline(store.id);
                          }} />
                        {canManageHiring && (
                          <TableActionButton action="link" label={`Link hiring for ${store.name}`}
                            onClick={() => setLinkHiringStore(store)} />
                        )}
                        {/* Disabled while the store's own request has not ended: no second one is offered. */}
                        {canRaiseHiring && (
                          <TableActionButton action="opening" label={`Raise hiring for ${store.name}`}
                            disabled={hasRaiseInFlight(storeHiring.byStore.get(storeHiringKey(store.id)))}
                            title={hasRaiseInFlight(storeHiring.byStore.get(storeHiringKey(store.id))) ? RAISE_WORDS.rowControlBusy : undefined}
                            onClick={() => setRaiseHiringStore(store)} />
                        )}
                        {/* For a person who may ask and may not raise. Disabled while the store's own
                            hiring request is undecided, or a raise for it has not ended. */}
                        {canAskHiring && (
                          <TableActionButton action="submit" label={REQUEST_WORDS.rowControl(store.name)}
                            disabled={hiringRequests.byStore.has(storeHiringKey(store.id))
                              || hasRaiseInFlight(storeHiring.byStore.get(storeHiringKey(store.id)))}
                            title={hiringRequests.byStore.has(storeHiringKey(store.id))
                              ? REQUEST_WORDS.rowControlWaiting
                              : hasRaiseInFlight(storeHiring.byStore.get(storeHiringKey(store.id))) ? RAISE_WORDS.rowControlBusy : undefined}
                            onClick={() => setAskHiringStore(store)} />
                        )}
                        {/* The store's hiring window, as the word in its Hiring cell opens it (its openings,
                            each with Details, Team and Candidates): only for a store with hiring followed,
                            being raised or waiting. The same control as on Undeployed Stores. */}
                        {openHiringOf(store).status && (
                          <TableActionButton action="openings" label={STORE_ROW_HIRING_WORDS.open(store.name)}
                            title={openHiringOf(store).openHover ?? undefined}
                            onClick={() => setHiringPanelStore(store)} />
                        )}
                      </TableActionGroup>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeStoreModal}
        title={modalMode === 'create' ? 'Add New Store' : 'Edit Store'}
        size="lg"
        footer={(
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={closeStoreModal}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="store-management-form"
              disabled={isSubmitting}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {modalMode === 'create' ? 'Save Store' : 'Save Changes'}
            </button>
          </div>
        )}
      >
        <form id="store-management-form" className="space-y-4" onSubmit={handleStoreSubmit}>
          {formError && <FormErrorBanner message={formError} />}

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="store-name">
              Store Name
            </label>
            <input
              id="store-name"
              type="text"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              placeholder="e.g. Reliance Digital @ Shivalik"
              required
              value={formState.name}
              onChange={(event) => handleFormChange('name', event.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700" htmlFor="trade">
                Trade
              </label>
              <select
                id="trade"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                value={formState.trade}
                onChange={(event) => handleFormChange('trade', event.target.value)}
              >
                <option value="">Select trade…</option>
                {/* From the Trades master, NOT from the trades already present in
                    the data — otherwise a newly-added trade cannot be picked at
                    all, which is what forced new trades in through bulk upload. */}
                {formTradeOptions.map((option) => (
                  <option key={option}>{option}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700" htmlFor="category">
                Store Category
              </label>
              <select
                id="category"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                value={formState.categoryId}
                onChange={(event) => handleFormChange('categoryId', event.target.value)}
              >
                <option value="">Select category…</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700" htmlFor="store-code">
                Store Code
              </label>
              <input
                id="store-code"
                type="text"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                placeholder="Enter Store Code"
                value={formState.storeCode}
                onChange={(event) => handleFormChange('storeCode', event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700" htmlFor="status">
                Status
              </label>
              <select
                id="status"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                value={formState.status}
                onChange={(event) => handleFormChange('status', event.target.value)}
              >
                {/* A store being created has nobody deployed, so nobody to redeploy: the
                    API refuses that status there. Offered on Edit only. */}
                {(modalMode === 'create' ? STORE_STATUS_OPTIONS : filtersConfig.status).map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {formState.status === STORE_STATUS_REDEPLOY && (
                <p className="text-xs text-gray-500">{REDEPLOY_STATUS_HELP}</p>
              )}
            </div>
          </div>
          {/* Staffing status on its own row — a seven-option list needs the width, and
              pairing it with Status would imply the two are alternatives. */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="staffing-status">
              Staffing Status
            </label>
            <select
              id="staffing-status"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              value={formState.staffingStatus}
              onChange={(event) => handleFormChange('staffingStatus', event.target.value)}
            >
              <option value="">— Not set —</option>
              {filtersConfig.staffingStatus.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500">
              Where the store sits in the manpower pipeline. Independent of Status — a store can be Active and still hiring.
            </p>
          </div>
          {/* State and City in one row */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700" htmlFor="state-select">
                State
              </label>
              {/* Type-to-filter: this list is every state, and scrolling it to find one
                  was the complaint. `value` stays pinned to the bare state name — the
                  label carries the region for display (and for searching) only, and must
                  never become the saved value. */}
              <SearchableSelect
                id="state-select"
                value={formState.state}
                onChange={(next) => handleFormChange('state', next)}
                options={formStateOptions.map((option) => {
                  const region = regionByStateKey.get(normalizeLocationKey(option));
                  return { value: option, label: region ? `${option} — ${region}` : option };
                })}
                placeholder="Select state…"
                searchPlaceholder="Search state or region…"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700" htmlFor="city">
                City {isLoadingCities && <span className="text-xs text-gray-500">(loading...)</span>}
              </label>
              <div className="relative">
                {/* Same rule as the state select: `value` stays the bare city name, the
                    tier is display + search only. A state can carry hundreds of cities,
                    so this one matters even more than the state field. */}
                <SearchableSelect
                  id="city"
                  value={formState.city}
                  onChange={(next) => handleFormChange('city', next)}
                  options={formAvailableCities.map((city) => {
                    const tier = formCityTierByKey.get(normalizeLocationKey(city));
                    return { value: city, label: tier ? `${city} — ${tier}` : city };
                  })}
                  placeholder={!formState.state ? 'Select state first…' : isLoadingCities ? 'Loading cities…' : 'Select city…'}
                  searchPlaceholder="Search city or tier…"
                  disabled={!formState.state || isLoadingCities}
                />
                {isLoadingCities && (
                  <div className="absolute inset-y-0 right-8 flex items-center">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Address in its own row with autocomplete */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="address">
              Address {isLoadingAddressSuggestions && <span className="text-xs text-gray-500">(searching...)</span>}
            </label>
            <div className="relative">
              <input
                id="address"
                type="text"
                className={`w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 ${!formState.state || !formState.city ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder={!formState.state || !formState.city ? "Please select state and city first" : "Start typing address..."}
                value={formState.address}
                onChange={(event) => handleAddressInputChange(event.target.value)}
                onBlur={() => setTimeout(() => setShowAddressSuggestions(false), 200)}
                onFocus={() => addressSuggestions.length > 0 && setShowAddressSuggestions(true)}
                disabled={!formState.state || !formState.city}
              />
              {isLoadingAddressSuggestions && (
                <div className="absolute inset-y-0 right-3 flex items-center">
                  <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                </div>
              )}
              {/* Address Suggestions Dropdown */}
              {showAddressSuggestions && addressSuggestions.length > 0 && (
                <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg max-h-60 overflow-auto">
                  {addressSuggestions.map((suggestion, index) => (
                    <button
                      key={suggestion.placeId || index}
                      type="button"
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleAddressSelect(suggestion);
                      }}
                    >
                      <div className="font-medium text-gray-900 truncate">{suggestion.address}</div>
                    </button>
                  ))}
                  <div className="flex justify-end px-2.5 pb-[5px] pt-2.5">
                    <img
                      src="/google-maps-logo.svg"
                      alt="Google Maps"
                      className="h-[18px] w-[98px]"
                    />
                  </div>
                </div>
              )}
            </div>
            {!formState.city || !formState.state ? (
              <p className="text-xs text-amber-600">Please select state and city first to enable address search</p>
            ) : (
              <p className="text-xs text-gray-500">Type at least 3 characters to search for addresses</p>
            )}
          </div>
          {/* Pincode, Latitude, Longitude in one row */}
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700" htmlFor="pincode">
                Pincode
              </label>
              <input
                id="pincode"
                type="text"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                placeholder=""
                value={formState.pincode}
                onChange={(event) => handleFormChange('pincode', event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700" htmlFor="latitude">
                Latitude
              </label>
              <input
                id="latitude"
                type="text"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                placeholder="Auto-filled from address"
                value={formState.latitude}
                onChange={(event) => handleFormChange('latitude', event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700" htmlFor="longitude">
                Longitude
              </label>
              <input
                id="longitude"
                type="text"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                placeholder="Auto-filled from address"
                value={formState.longitude}
                onChange={(event) => handleFormChange('longitude', event.target.value)}
              />
            </div>
          </div>
          <p className="text-xs text-gray-500">
            These fields are auto-filled when you select an address from suggestions. You can also enter them manually.
          </p>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="attendanceRadius">
              Attendance Radius (meters)
            </label>
            <input
              id="attendanceRadius"
              type="number"
              min="10"
              max="1000"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              placeholder="100"
              value={formState.attendanceRadius}
              onChange={(event) => handleFormChange('attendanceRadius', event.target.value)}
            />
            <p className="text-xs text-gray-500">
              Radius in meters within which employees can mark attendance (default: 100m)
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700" htmlFor="shiftStartTime">
                Store Timing
              </label>
              <input
                id="shiftStartTime"
                type="time"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                value={formState.shiftStartTime}
                onChange={(event) => handleFormChange('shiftStartTime', event.target.value)}
              />
              <p className="text-xs text-gray-500">
                The respective timing is based on when the store usually opens or starts shift.
              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700" htmlFor="storeWeekOff">
                Store Week Off
              </label>
              <select
                id="storeWeekOff"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                value={formState.storeWeekOff}
                onChange={(event) => handleFormChange('storeWeekOff', event.target.value)}
              >
                <option value="">No weekly off</option>
                <option value="0">Sunday</option>
                <option value="1">Monday</option>
                <option value="2">Tuesday</option>
                <option value="3">Wednesday</option>
                <option value="4">Thursday</option>
                <option value="5">Friday</option>
                <option value="6">Saturday</option>
              </select>
              <p className="text-xs text-gray-500">
                Day of the week this store is generally closed. If Week Off Auto-apply is enabled for a role, staff at
                this store get a Week Off request for this day each week, sent to their manager; they can edit or
                cancel it.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="compliancePolicy">
              Store Compliance / Grooming Guidelines
            </label>
            <textarea
              id="compliancePolicy"
              rows={4}
              maxLength={4000}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              placeholder="e.g. Footwear: formal black shoes only, no sneakers or sandals. Attire: store-issued uniform shirt tucked in; no personal T-shirts. Grooming: hair neatly tied for all staff."
              value={formState.compliancePolicy}
              onChange={(event) => handleFormChange('compliancePolicy', event.target.value)}
            />
            <p className="text-xs text-gray-500">
              Store-specific dress-code, footwear and appearance rules. These are sent to the AI grooming
              check for every employee at this store and factored into their grooming score. Leave blank for none.
            </p>
          </div>

          {/* Project-defined extra attributes. Renders nothing when the project has
              defined none, so this form is unchanged for projects not using them. */}
          <StoreCustomFieldInputs
            fields={customFieldDefs}
            values={customFieldValues}
            onChange={(fieldId, value) =>
              setCustomFieldValues((prev) => ({ ...prev, [fieldId]: value }))
            }
          />
        </form>
      </Modal>

      {/* "Store added": the Add New Store window after it made a new store, when hiring may
          be raised or asked for here. */}
      {storeAdded && (
        <Modal
          isOpen
          onClose={() => setStoreAdded(null)}
          title={STORE_ADDED_WORDS.title}
          size="lg"
          footer={(
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                onClick={() => setStoreAdded(null)}
              >
                {STORE_ADDED_WORDS.done}
              </button>
              {storeAddedMode && (
                <button
                  type="button"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
                  onClick={() => raiseForAddedStore(storeAdded.store, storeAddedMode)}
                >
                  {storeAddedMode === 'raise' ? STORE_ADDED_WORDS.raise : STORE_ADDED_WORDS.ask}
                </button>
              )}
            </div>
          )}
        >
          <p className="text-sm text-gray-700">{STORE_ADDED_WORDS.line(storeAdded.store.name, storeAdded.store.storeCode)}</p>
        </Modal>
      )}

      <Modal
        isOpen={isDeployModalOpen}
        onClose={closeDeployModal}
        size="xl"
        title="Deploy Employee"
        headerContent={
          <div className="flex items-center justify-between rounded-t-lg bg-primary-600 px-6 py-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-100">Store Operations</p>
              <h3 className="text-lg font-semibold text-white">Deploy Employee</h3>
              <p className="text-xs text-primary-100">Manage workforce for {deployModalStore?.name || 'Store'}</p>
            </div>
            <button
              onClick={closeDeployModal}
              className="rounded-full p-2 text-primary-100 transition hover:text-white"
              aria-label="Close deploy employee modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        }
        footer={(
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={closeDeployModal}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeployEmployee}
              disabled={!selectedCandidateCode || isSubmitting}
              // text-white belongs to the enabled branch only: left on the base it also sat
              // on the disabled button beside text-gray-500, and Tailwind emits text-white
              // later, so the label stayed white on the grey button.
              className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition-colors ${
                selectedCandidateCode && !isSubmitting 
                  ? 'bg-primary-600 text-white hover:bg-primary-700' 
                  : 'cursor-not-allowed bg-gray-300 text-gray-500'
              }`}
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Deploy Employee
            </button>
          </div>
        )}
      >
        {/* A warning, not a block: such a store is still an active store in every
            other respect, so deploying to it stays allowed. */}
        {deployModalStatus === STORE_STATUS_REDEPLOY && (
          <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
            This store is marked {STORE_STATUS_REDEPLOY} — the people deployed here are due to be moved to another store.
          </p>
        )}
        {/* A failed Remove or Deploy used to show nothing in this modal: Remove only
            logged, and Deploy set formError, which this modal never rendered. */}
        {deployModalError && (
          <div className="mb-4">
            <FormErrorBanner message={deployModalError} />
          </div>
        )}
        <div className="grid gap-6 lg:grid-cols-5">
          <div className="space-y-6 rounded-2xl bg-gray-50/60 p-4 lg:col-span-3">
            <section className="rounded-lg border border-gray-100 bg-white">
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">●</span>
                  Currently Deployed Employees ({deployedEmployees.length})
                </div>
                {/* "Updated just now" only once the store's people were read afresh. */}
                {deployedRead === 'checking' ? (
                  <span className="text-xs font-medium text-gray-400" title="Reading who is deployed at this store now.">Checking…</span>
                ) : deployedRead === 'fresh' ? (
                  <span className="text-xs font-medium text-gray-500">Updated just now</span>
                ) : null}
              </div>
              <div className="space-y-3 px-5 py-4">
                {deployedEmployees.length === 0 ? (
                  <p className="text-sm text-gray-500">No employees currently deployed to this store.</p>
                ) : (
                  deployedEmployees.map((employee) => (
                    <div key={employee.code} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-gray-50/70 px-4 py-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <p className="text-base font-semibold text-gray-900">{employee.name}</p>
                          {/* Was hardcoded to "Active", so someone who had left 1Office still
                              read as active here while the Employees page had already dropped
                              them. Their mapping survives until the reconcile sweep unmaps it. */}
                          {employee.isActive ? (
                            <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700">Active</span>
                          ) : (
                            <span
                              className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
                              title="No longer on the 1Office roster. The mapping is removed automatically by the next roster sync, or Remove it now."
                            >
                              Exited — mapping not yet removed
                            </span>
                          )}
                        </div>
                        <div className="grid gap-y-1 text-xs text-gray-600 sm:grid-cols-2">
                          <span>Code: {employee.code}</span>
                          <span>Role: {employee.role}</span>
                          <span>Joined: {employee.joined}</span>
                          <span>Salary: {employee.salary}</span>
                        </div>
                      </div>
                      {/* Two clicks: removal takes the employee off this store at once. The
                          second click names THIS row's employee, so the others deployed at
                          the store stay. */}
                      {removeConfirmCode === employee.code ? (
                        <div
                          role="group"
                          aria-label={`Confirm removing ${employee.name}`}
                          className="flex flex-wrap items-center gap-2"
                        >
                          <span className="text-xs font-medium text-gray-700">Remove from this store?</span>
                          {/* Focus lands on the safe answer: the Remove that was clicked
                              has just left the page, and keyboard focus with it. */}
                          <button
                            type="button"
                            autoFocus
                            onClick={() => {
                              refocusRemoveRef.current = employee.code;
                              setRemoveConfirmCode(null);
                            }}
                            disabled={isSubmitting}
                            className="rounded-full border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                          >
                            No, keep
                          </button>
                          <button
                            type="button"
                            onClick={() => deployModalStore && handleRemoveEmployee(deployModalStore.id, employee.code)}
                            disabled={isSubmitting}
                            className="inline-flex items-center gap-1 rounded-full bg-red-600 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                          >
                            {isSubmitting && <Loader2 className="h-3 w-3 animate-spin" />}
                            Yes, remove
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          // Back from a question that closed without a removal ("No,
                          // keep", or a removal that failed): take focus again, which
                          // went with the button that was just clicked. The mark is
                          // cleared only once focus has landed, since a button that is
                          // still disabled cannot take it.
                          ref={(el) => {
                            if (el && refocusRemoveRef.current === employee.code) {
                              el.focus();
                              if (document.activeElement === el) refocusRemoveRef.current = null;
                            }
                          }}
                          onClick={() => setRemoveConfirmCode(employee.code)}
                          disabled={isSubmitting}
                          aria-label={`Remove ${employee.name}`}
                          className="rounded-full border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </section>

            <section className="rounded-lg border border-gray-100 bg-white p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-50 text-primary-600">ℹ️</span>
                  Deploy Additional Employee
                </div>
                <p className="text-xs font-medium text-gray-500">{deployedEmployees.length} already deployed to this store</p>
              </div>
              <div className="mt-4 flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white shadow">👥</span>
                Available Employees: {availableEmployees.length}
              </div>
              <div className="mt-4 space-y-3">
                <label className="text-xs font-medium text-gray-600" htmlFor="deploy-employee-search">
                  Search Employee
                </label>
                <input
                  id="deploy-employee-search"
                  type="text"
                  placeholder="Search by name, code, or city…"
                  value={employeeSearchTerm}
                  onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <div className="mt-4 max-h-[300px] overflow-y-auto">
                {/* Nobody is offered before the store's people are known: the picker leaves them out. */}
                {isLoadingEmployees || deployedRead === 'checking' ? (
                  <div className="flex items-center justify-center py-8">
                    <InlineLoader label="Loading employees..." />
                  </div>
                ) : filteredEmployees.length === 0 ? (
                  <p className="py-4 text-center text-sm text-gray-500">No available employees found.</p>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {filteredEmployees.map((employee) => {
                      const isSelected = selectedCandidateCode === employee.code;
                      return (
                        <button
                          type="button"
                          key={employee.code}
                          onClick={() => setSelectedCandidateCode(employee.code)}
                          className={`text-left rounded-xl border p-4 shadow-sm transition-all ${
                            isSelected
                              ? 'border-primary-400 bg-primary-50 ring-2 ring-primary-200'
                              : 'border-gray-200 bg-white hover:border-primary-300 hover:shadow-md'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <p className="text-sm font-bold text-gray-900">{employee.name || 'Unnamed'}</p>
                              <p className="text-xs text-gray-500 mt-0.5">Code: {employee.code}</p>
                              {employee.leavingOn && (
                                <p className="text-[11px] text-gray-400 mt-0.5">Leaving {formatHiringDay(employee.leavingOn)}</p>
                              )}
                              {employee.multiStore && (
                                <span className="mt-1 inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                                  Roaming · already on other stores
                                </span>
                              )}
                            </div>
                            {isSelected && (
                              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary-500 text-white">
                                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </span>
                            )}
                          </div>
                          <div className="space-y-1.5 text-xs">
                            <div className="flex justify-between">
                              <span className="text-gray-500">Joining:</span>
                              <span className="text-gray-700 font-medium">{employee.join || '—'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Designation:</span>
                              <span className="text-gray-700 font-medium text-right max-w-[150px] truncate">{employee.designation || '—'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">City:</span>
                              <span className="text-gray-700 font-medium">{employee.city || '—'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">CTC:</span>
                              <span className="text-gray-700 font-medium">{employee.ctc || '—'}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          </div>

          <aside className="space-y-4 lg:col-span-2">
            <div className="rounded-lg border border-gray-100 bg-white p-5">
              <h3 className="text-sm font-semibold text-gray-900">Store Information</h3>
              <dl className="mt-3 space-y-2 text-sm text-gray-600">
                <div className="flex justify-between"><dt>Store Name:</dt><dd className="text-right">{deployModalStore?.name || '—'}</dd></div>
                <div className="flex justify-between"><dt>Store Category:</dt><dd>{deployModalStore?.category || '—'}</dd></div>
                <div className="flex justify-between">
                  <dt>Status:</dt>
                  <dd>
                    {/* No `border` width class here, so the map's border colours are inert
                        and Active / Inactive look as they did under the old ternary. */}
                    <span className={`rounded-full px-2 py-0.5 text-xs ${
                      STORE_STATUS_BADGE[deployModalStatus ?? ''] ?? STORE_STATUS_OTHER_BADGE
                    }`}>
                      {deployModalStatus || '—'}
                    </span>
                  </dd>
                </div>
                <div className="flex justify-between"><dt>City:</dt><dd>{deployModalStore?.city || '—'}</dd></div>
                <div className="flex justify-between"><dt>Pincode:</dt><dd>{deployModalStore?.pincode || '—'}</dd></div>
              </dl>
            </div>
            {/* Eligibility still matters to whoever is deploying; the payout band is
                the part that is gated, so the card becomes eligibility-only rather
                than disappearing and taking the criteria with it. */}
            {(canSeeMoney || deployModalStore?.eligibilityCriteria) && (
              <div className="rounded-lg border border-gray-100 bg-white p-5">
                <h3 className="text-sm font-semibold text-gray-900">
                  {canSeeMoney ? 'Payout Information' : 'Store Requirements'}
                </h3>
                {canSeeMoney && (
                  <p className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                    {deployModalStore?.payoutMin && deployModalStore?.payoutMax
                      ? `${currency(deployModalStore.payoutMin)} - ${currency(deployModalStore.payoutMax)}`
                      : '—'}
                  </p>
                )}
                {deployModalStore?.eligibilityCriteria && (
                  <div className={canSeeMoney ? 'mt-3' : 'mt-2'}>
                    <p className="text-xs font-medium text-gray-600">Eligibility Criteria:</p>
                    <p className="mt-1 text-xs text-gray-500">{deployModalStore.eligibilityCriteria}</p>
                  </div>
                )}
              </div>
            )}
          </aside>
        </div>
      </Modal>

      <Modal
        isOpen={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        size="lg"
        title="Store Status & Timeline"
        footer={(
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={() => setIsStatusModalOpen(false)}
            >
              Close
            </button>
            <button
              type="button"
              onClick={handleStatusUpdate}
              disabled={!statusModalDirty || isSubmitting}
              // text-white belongs to the enabled branch only: left on the base it also sat
              // on the disabled button beside text-gray-500, and Tailwind emits text-white
              // later, so the label stayed white on the grey button.
              className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition-colors ${
                statusModalDirty && !isSubmitting
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'cursor-not-allowed bg-gray-300 text-gray-500'
              }`}
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Update Status
            </button>
          </div>
        )}
      >
        {statusModalStore ? (
          <div className="space-y-5">
            {/* One line instead of the old details card: the card repeated what the
                row the user just clicked already showed, and the timeline is the
                reason this modal exists. */}
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-b border-gray-100 pb-3">
              <h3 className="text-base font-semibold text-gray-900">{statusModalStore.name}</h3>
              <span className="text-xs text-gray-500">
                {statusModalStore.storeCode || 'No code'}
                {statusModalStore.city ? ` · ${statusModalStore.city}` : ''}
                {statusModalStore.category ? ` · ${statusModalStore.category}` : ''}
              </span>
            </div>

            {formError && <FormErrorBanner message={formError} />}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700" htmlFor="store-status-select">
                  Status
                </label>
                <select
                  id="store-status-select"
                  value={statusFormState}
                  onChange={(event) => setStatusFormState(event.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                >
                  {filtersConfig.status.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                {statusFormState === STORE_STATUS_REDEPLOY && (
                  <p className="text-xs text-gray-500">{REDEPLOY_STATUS_HELP}</p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700" htmlFor="store-staffing-select">
                  Staffing Status
                </label>
                <select
                  id="store-staffing-select"
                  value={staffingFormState}
                  onChange={(event) => setStaffingFormState(event.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                >
                  <option value="">— Not set —</option>
                  {filtersConfig.staffingStatus.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700" htmlFor="store-status-remark">
                Remark <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <textarea
                id="store-status-remark"
                rows={2}
                value={statusRemark}
                onChange={(event) => setStatusRemark(event.target.value)}
                placeholder="Why is this changing? Shown against this entry in the timeline below."
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900">Timeline</h4>
                {timeline.length > 0 && (
                  <span className="text-xs text-gray-400">{timeline.length} event{timeline.length === 1 ? '' : 's'}</span>
                )}
              </div>

              {timelineLoading ? (
                <div className="mt-3 space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex gap-3">
                      <Skeleton className="h-4 w-28 shrink-0" />
                      <Skeleton className="h-4 flex-1" />
                    </div>
                  ))}
                </div>
              ) : timelineError ? (
                <p className="mt-3 text-sm text-gray-500">{timelineError}</p>
              ) : timeline.length === 0 ? (
                <p className="mt-3 rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
                  Nothing recorded yet. Store history starts from the first change made after this
                  feature shipped — earlier edits overwrote the store in place and left no trail.
                </p>
              ) : (
                <ol className="mt-3 max-h-72 space-y-0 overflow-y-auto pr-1">
                  {timeline.map((ev, i) => (
                    <li key={ev.id} className="flex gap-3">
                      <div className="w-32 shrink-0 pt-0.5 text-right text-xs text-gray-500">
                        {formatTimelineWhen(ev.at, ev.dateOnly)}
                      </div>
                      <div className="flex flex-col items-center">
                        <div className={`z-10 mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${TIMELINE_DOT[ev.eventType] || 'bg-gray-300'}`} />
                        {i < timeline.length - 1 && <div className="w-px flex-1 bg-gray-200" />}
                      </div>
                      <div className="flex-1 pb-5">
                        {ev.eventType === 'employee_deployed' && (
                          <p className="text-sm text-gray-900">
                            Deployed <span className="font-medium">{ev.employeeName || ev.employeeCode}</span>
                            {ev.employeeName && ev.employeeCode ? <span className="text-gray-500"> ({ev.employeeCode})</span> : null}
                          </p>
                        )}
                        {ev.eventType === 'employee_undeployed' && (
                          <p className="text-sm text-gray-900">
                            Removed <span className="font-medium">{ev.employeeName || ev.employeeCode}</span>
                            {ev.employeeName && ev.employeeCode ? <span className="text-gray-500"> ({ev.employeeCode})</span> : null}
                          </p>
                        )}
                        {ev.eventType === 'store_created' && (
                          <p className="text-sm font-medium text-gray-900">Store created</p>
                        )}
                        {ev.eventType === 'store_updated' && ev.changes.length === 0 && (
                          <p className="text-sm text-gray-900">Store updated</p>
                        )}
                        {ev.changes.length > 0 && (
                          <ul className="space-y-1">
                            {ev.changes.map((c) => (
                              <li key={c.field} className="text-sm text-gray-900">
                                <span className="font-medium">{c.label}</span>
                                {ev.eventType === 'store_created' ? (
                                  <>: <span className="text-gray-700">{c.to || '—'}</span></>
                                ) : (
                                  <>
                                    {': '}
                                    <span className="text-gray-500 line-through">{c.from || '—'}</span>
                                    {' → '}
                                    <span className="text-gray-900">{c.to || '—'}</span>
                                  </>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                        {ev.note && (
                          <p className="mt-1 text-sm italic text-gray-600">“{ev.note}”</p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">{timelineActorLabel(ev)}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No store selected.</p>
        )}
      </Modal>

      {hiringOn && hiringPanelStore && (
        <HiringPanel
          projectId={selectedProjectId!}
          subject={{ kind: 'store', storeName: hiringPanelStore.name, storeCode: hiringPanelStore.storeCode, storeId: hiringPanelStore.id }}
          // Read off the list as it is NOW, so a link made or stopped shows without reopening.
          links={storeHiring.byStore.get(storeHiringKey(hiringPanelStore.id)) ?? NO_HIRING_LINKS}
          canManage={canManageHiring}
          // Offered only to a holder of "Raise & cancel hiring" whom the list answer says may cancel here.
          canCancel={hiringAccess.canRaise && storeHiring.cancel.canCancel}
          onClose={() => setHiringPanelStore(null)}
          // A stopped link leaves its row at once; the whole list is then read again
          // behind it. With no link (someone else stopped it first) the re-read does both.
          // The hiring requests are read again with it: a create that is closed as not
          // created puts its hiring request back to waiting, and that must show here at
          // once - on the row, as a card in this panel and in the Hiring tab's count -
          // before anyone raises the same hiring a second time.
          onChanged={(stopped) => {
            if (stopped) storeHiring.apply(stopped);
            reloadHiringLists();
            // A hiring closed as filled placed someone at this store: its team is read again -
            // for the project the placing was made in, and only while that project is still on
            // show. A placing can take seconds, and its answer arrives whether or not the panel
            // is still open: read for a project the page has left, these stores would be drawn
            // under the other project's name.
            if (stopped?.closedAs === 'filled' && projectOnShow.current === selectedProjectId) fetchStores();
          }}
          onLinkHiring={() => {
            setLinkHiringStore(hiringPanelStore);
            setHiringPanelStore(null);
          }}
          // Offered only to someone the server says may raise hiring here.
          onRaiseHiring={canRaiseHiring ? () => {
            setRaiseHiringStore(hiringPanelStore);
            setHiringPanelStore(null);
          } : undefined}
          onHiringGone={() => forgetHiringAccess(selectedProjectId)}
          // The store's hiring request that waits for confirmation, drawn above its openings.
          requests={requestsShown ? waitingRequestsOf(hiringRequests.byStore.get(storeHiringKey(hiringPanelStore.id))) : NO_HIRING_REQUESTS}
          onOpenRequest={(request) => {
            setHiringRequestOpen(request);
            setHiringPanelStore(null);
          }}
          // Offered only to someone who may ask and may not raise.
          onAskHiring={canAskHiring ? () => {
            setAskHiringStore(hiringPanelStore);
            setHiringPanelStore(null);
          } : undefined}
          // An opening deleted in Recruitment: the same two windows, handed the deleted link.
          onRaiseReplacing={canRaiseHiring ? (deleted) => {
            setHiringReplacing({ storeId: hiringPanelStore.id, link: deleted });
            setRaiseHiringStore(hiringPanelStore);
            setHiringPanelStore(null);
          } : undefined}
          onAskReplacing={canAskHiring ? (deleted) => {
            setHiringReplacing({ storeId: hiringPanelStore.id, link: deleted });
            setAskHiringStore(hiringPanelStore);
            setHiringPanelStore(null);
          } : undefined}
        />
      )}

      {/* The Raise hiring window in ask mode: the form is saved as a hiring request for a
          person who may raise hiring to confirm. Nothing is sent to Recruitment from it. */}
      {canAskHiring && askHiringStore && (
        <RaiseHiringModal
          mode="ask"
          entry="store"
          projectId={selectedProjectId!}
          subject={raiseSubjectOf(storedNow(askHiringStore))}
          // "Edit store" on the store card: the store's own Edit Store window, over this one.
          onEditStore={() => openEditModal(storedNow(askHiringStore))}
          // Opened from "Store added": the reason is New store.
          fixedReason={askHiringStore === newStoreToRaise ? 'new_store' : undefined}
          // A raise already in flight for the store is shown in place of a form.
          links={storeHiring.byStore.get(storeHiringKey(askHiringStore.id)) ?? NO_HIRING_LINKS}
          // Asked for from a deleted opening: the result says it stays followed.
          replacingDeleted={replacingFor(askHiringStore)}
          onClose={() => setAskHiringStore(null)}
          onChanged={(link) => {
            storeHiring.apply(link);
            storeHiring.reload();
          }}
          // The hiring request shows on its row at once, from the answer itself. The
          // window stays open: it is showing that it was sent for confirmation.
          onAsked={(request) => hiringRequests.apply(request)}
          onOutOfDate={reloadHiringLists}
          onHiringGone={() => forgetHiringAccess(selectedProjectId)}
          // Raising was switched off under the window: it closes, and hiring requests go with it.
          onRaiseGone={() => {
            setRaiseGoneFor(selectedProjectId);
            forgetHiringRequests(selectedProjectId);
            setAskHiringStore(null);
            storeHiring.reload();
          }}
        />
      )}

      {hiringOn && hiringAccess.canSeeRequests && hiringRequestOpen && (
        <HiringRequestWindow
          projectId={selectedProjectId!}
          // As the list has it now; once it has left the list, as it was opened.
          request={hiringRequests.requests.find((r) => r.id === hiringRequestOpen.id) ?? hiringRequestOpen}
          can={hiringRequests.can}
          onClose={() => setHiringRequestOpen(null)}
          onRequest={(request) => hiringRequests.apply(request)}
          // A confirm made a link: it shows on its row at once, and both lists are read again behind it.
          onLink={(link) => {
            storeHiring.apply(link);
            reloadHiringLists();
          }}
          onOutOfDate={reloadHiringLists}
          onHiringGone={() => forgetHiringAccess(selectedProjectId)}
          onRaiseGone={() => {
            setRaiseGoneFor(selectedProjectId);
            forgetHiringRequests(selectedProjectId);
            setHiringRequestOpen(null);
            storeHiring.reload();
          }}
        />
      )}

      {hiringAccess.canRaise && raiseHiringStore && (
        <RaiseHiringModal
          entry="store"
          projectId={selectedProjectId!}
          subject={raiseSubjectOf(storedNow(raiseHiringStore))}
          // "Edit store" on the store card: the store's own Edit Store window, over this one.
          onEditStore={() => openEditModal(storedNow(raiseHiringStore))}
          // Opened from "Store added": the reason is New store.
          fixedReason={raiseHiringStore === newStoreToRaise ? 'new_store' : undefined}
          // A request already being raised for the store is shown in place of a form.
          links={storeHiring.byStore.get(storeHiringKey(raiseHiringStore.id)) ?? NO_HIRING_LINKS}
          // Raised again from a deleted opening: once this raise made an opening, that one is no longer followed.
          replacingDeleted={replacingFor(raiseHiringStore)}
          onClose={() => setRaiseHiringStore(null)}
          // The request shows on its row at once, from the answer itself; the whole list
          // is then read again behind it. The window stays open: it is showing the result.
          onChanged={(link) => {
            storeHiring.apply(link);
            // A waiting hiring request for the store now has hiring beside it: its card says so.
            reloadHiringLists();
          }}
          onOutOfDate={reloadHiringLists}
          onHiringGone={() => forgetHiringAccess(selectedProjectId)}
          // Raising was switched off under the window: it closes, and its control goes.
          // Hiring requests exist only where Raise is on: they go with it.
          onRaiseGone={() => {
            setRaiseGoneFor(selectedProjectId);
            forgetHiringRequests(selectedProjectId);
            setRaiseHiringStore(null);
            storeHiring.reload();
          }}
          // The request in the way under a budget, when it is a store's on this page.
          openRequest={(linkId) => {
            for (const [key, links] of storeHiring.byStore) {
              if (!links.some((l) => l.id === linkId)) continue;
              const store = stores.find((s) => storeHiringKey(s.id) === key);
              if (!store) return null;
              return () => {
                setRaiseHiringStore(null);
                setHiringPanelStore(store);
              };
            }
            return null;
          }}
          // Recruitment already has an unlinked opening with the store's name on it: this
          // window closes, and Link hiring opens for the same store on that opening.
          onLinkInstead={({ budgetId, openingId }) => {
            setLinkHiringPick({ budgetId, openingId });
            setLinkHiringStore(raiseHiringStore);
            setRaiseHiringStore(null);
          }}
        />
      )}

      {canManageHiring && linkHiringStore && (
        <LinkHiringModal
          projectId={selectedProjectId!}
          subject={{ kind: 'store', storeId: linkHiringStore.id, storeName: linkHiringStore.name, storeCode: linkHiringStore.storeCode }}
          // What the store already follows is not offered to it a second time.
          followed={storeHiring.byStore.get(storeHiringKey(linkHiringStore.id)) ?? NO_HIRING_LINKS}
          initial={linkHiringPick ?? undefined}
          onClose={() => {
            setLinkHiringStore(null);
            setLinkHiringPick(null);
          }}
          // The new link shows on its row at once, from the answer to the link itself;
          // the whole list is then read again behind it.
          onLinked={(link) => {
            setLinkHiringPick(null);
            setLinkHiringStore(null);
            if (link) storeHiring.apply(link);
            storeHiring.reload();
          }}
          onOutOfDate={storeHiring.reload}
          onHiringGone={() => forgetHiringAccess(selectedProjectId)}
        />
      )}

      {/* The hiring question when "Deploy Employee" is pressed: outside the table, so a re-read of the stores never closes it. */}
      {deployHiring.window}

      <BulkUploadModal
        isOpen={isBulkUploadModalOpen}
        onClose={() => setIsBulkUploadModalOpen(false)}
        onSuccess={fetchStores}
        projectId={selectedProjectId!}
      />
    </div>
  );
}
